import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  Wallet, Plus, Search, Landmark, Check, Info, Bot, User, CheckCircle2, X, ChevronUp,
  Truck, CreditCard, Banknote, Pencil, Trash2, Receipt, Printer, Save, AlertCircle,
  Clock, Building2, Package2, DollarSign
} from 'lucide-react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import POPrint from '@/components/purchasing/POPrint';
import { generatePONumber } from '@/lib/poNumberGenerator';
import { toast } from 'react-hot-toast';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import { useKurs } from '@/hooks/useKurs';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import AsaArrivalConfirmModal from '@/components/purchasing/AsaArrivalConfirmModal';

const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

const parseRate = (val, fallback = 16000) => {
  if (val == null || val === '') return fallback;
  if (typeof val === 'number') return val;
  const str = String(val).trim();
  if (/^\d{1,3}(\.\d{3})+$/.test(str)) {
    return parseFloat(str.replace(/\./g, ''));
  }
  const cleaned = str.replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) || parsed <= 0 ? fallback : parsed;
};

const getRequestDateDisplay = (item) => {
  if (!item) return null;
  let d = null;
  if (item.created_date) d = new Date(item.created_date);
  else if (item.created_at) d = new Date(item.created_at);
  else if (item.tanggal_pengajuan) d = new Date(item.tanggal_pengajuan);
  else if (item.date) d = new Date(item.date);
  else if (item.po_number) {
    const parts = item.po_number.split('-');
    const datePart = parts.find(p => p.length === 6 && !isNaN(Number(p)));
    if (datePart) {
      const day = parseInt(datePart.substring(0, 2), 10);
      const month = parseInt(datePart.substring(2, 4), 10) - 1;
      const year = parseInt('20' + datePart.substring(4, 6), 10);
      d = new Date(year, month, day);
    }
  }
  if (!d || isNaN(d.getTime())) return null;
  return format(d, 'dd MMM yyyy, HH:mm', { locale: id });
};

const getRequestDateOnly = (item) => {
  if (!item) return null;
  let d = null;
  if (item.created_date) d = new Date(item.created_date);
  else if (item.created_at) d = new Date(item.created_at);
  else if (item.tanggal_pengajuan) d = new Date(item.tanggal_pengajuan);
  else if (item.date) d = new Date(item.date);
  else if (item.po_number) {
    const parts = item.po_number.split('-');
    const datePart = parts.find(p => p.length === 6 && !isNaN(Number(p)));
    if (datePart) {
      const day = parseInt(datePart.substring(0, 2), 10);
      const month = parseInt(datePart.substring(2, 4), 10) - 1;
      const year = parseInt('20' + datePart.substring(4, 6), 10);
      d = new Date(year, month, day);
    }
  }
  if (!d || isNaN(d.getTime())) return null;
  return format(d, 'dd MMM yyyy', { locale: id });
};

const getPaymentDateDisplay = (item) => {
  if (!item) return null;
  let dStr = item.tanggal_bayar || item.tanggal_pembayaran || item.paid_at;
  
  if (!dStr && (item.status === 'Sudah Dibayar' || item.status === 'Dicicil')) {
    const matchCatatan = (item.catatan || '').match(/Tanggal\s*(?:Bayar|Pembayaran)\s*:\s*([\d\-/]+)/i);
    if (matchCatatan) {
      dStr = matchCatatan[1];
    } else {
      dStr = item.updated_date || item.updated_at;
    }
  }

  if (!dStr) return null;

  let d = null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dStr)) {
    const parts = dStr.substring(0, 10).split('-');
    d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  } else if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}/.test(dStr)) {
    const parts = dStr.split(/[/-]/);
    d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  } else {
    d = new Date(dStr);
  }

  if (!d || isNaN(d.getTime())) return null;
  return format(d, 'dd MMM yyyy', { locale: id });
};

export default function PurchasingRequestFunds() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { member, companySettings } = useSession();
  const { kurs, kursEuro } = useKurs();
  const isFinance = location.pathname.includes('/finance');

  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, dateFrom, dateTo]);

  // Modal states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [isEditingTotalTransfer, setIsEditingTotalTransfer] = useState(false);
  const [tempTotalTransfer, setTempTotalTransfer] = useState('');
  const [isEditingDiskonNamaDetail, setIsEditingDiskonNamaDetail] = useState(false);
  const [tempDiskonNamaDetail, setTempDiskonNamaDetail] = useState('');
  const [isEditingDiskonNominalDetail, setIsEditingDiskonNominalDetail] = useState(false);
  const [tempDiskonNominalDetail, setTempDiskonNominalDetail] = useState('');
  const [tempDiskonNominalDetailPct, setTempDiskonNominalDetailPct] = useState('');
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [tempItemDiskon, setTempItemDiskon] = useState('');
  const [tempItemDiskonPct, setTempItemDiskonPct] = useState('');

  // ASA Arrival Confirmation Modal State
  const [arrivalConfirmData, setArrivalConfirmData] = useState(null);
  const [isProcessingArrivalModal, setIsProcessingArrivalModal] = useState(false);

  const handleDownloadPO = useCallback((req) => {
    const data = {
      ...req,
      companySettings
    };
    setPrintData(data);
    const origTitle = document.title;
    const cleanPo = String(req.po_number || req.id || 'PO-Purchasing').replace(/[/\\?%*:|"<>]/g, '-').trim();
    const cleanVendor = String(req.supplier_name || req.nama_supplier || req.vendor || '').replace(/[/\\?%*:|"<>]/g, '-').trim();
    document.title = `${cleanPo}${cleanVendor ? ' - ' + cleanVendor : ''}`;
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = origTitle; }, 1000);
    }, 300);
  }, [companySettings]);

  // Mutasi states
  const { accountsMap: accounts } = useBankAccounts();
  const [showMutasiConfirm, setShowMutasiConfirm] = useState(false);
  const [mutasiAmount, setMutasiAmount] = useState(0);
  const [tempMutasiAmount, setTempMutasiAmount] = useState('');
  const [isEditingMutasi, setIsEditingMutasi] = useState(false);
  const [diskonNama, setDiskonNama] = useState('');
  const [diskonNominal, setDiskonNominal] = useState(0);
  const [mutasiAccount, setMutasiAccount] = useState('bca');
  const [mutasiDate, setMutasiDate] = useState(() => new Date().toLocaleDateString('en-CA'));

  // 7. Handlers for Tempo Approval
  const handleOpenTempoConfirm = () => {
    setShowTempoConfirm(true);
  };

  const handleFinalConfirmTempo = async () => {
    try {
      const payload = { 
        status: 'Menunggu Pembayaran',
        mutasi_account: tempoAccount // Store the COA account chosen for Hutang
      };

      if (selectedRequest.custom_total_biaya !== undefined) {
        payload.custom_total_biaya = selectedRequest.custom_total_biaya;
        payload.diskon_nama_temp = selectedRequest.diskon_nama_temp || 'Diskon Tambahan';
      }

      await updateRequestMut.mutateAsync({ id: selectedRequest.id, data: payload });
      
      // Update tickets so purchasing can receive them
      if (selectedRequest.batch_items) {
        const batchItems = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : selectedRequest.batch_items;
        for (const item of batchItems) {
          if (item.ticket?.id) {
            await updateTicketMut.mutateAsync({ id: item.ticket.id, data: { status: 'Dalam Pengiriman' } });
          }
        }
      }

      toast.success('Tempo disetujui, dan dicatat di akun hutang terpilih!');
      setShowTempoConfirm(false);
      setSelectedRequest(prev => ({ ...prev, ...payload }));
      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
    } catch (err) {
      toast.error('Gagal konfirmasi tempo: ' + err.message);
    }
  };

  const handleRejectTempo = async () => {
    if (confirm('Yakin ingin menolak pengajuan tempo ini? Tagihan ini akan dibatalkan.')) {
      try {
        await updateRequestMut.mutateAsync({ id: selectedRequest.id, data: { status: 'Ditolak' } });
        toast.success('Pengajuan Tempo berhasil ditolak.');
        setSelectedRequest(null);
      } catch (err) {
        toast.error('Gagal menolak pengajuan.');
      }
    }
  };

  const [showTempoConfirm, setShowTempoConfirm] = useState(false);
  const [tempoAccount, setTempoAccount] = useState('bca');

  // Split Invoice states
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [splitSelectedItems, setSplitSelectedItems] = useState(new Set());

  // Form states
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editItems, setEditItems] = useState([
    {
      id: 'item_1',
      type: 'bahan-cair',
      name: '',
      qty: 1,
      satuan: 'kg',
      mata_uang: 'IDR',
      harga_dollar: null,
      harga_euro: null,
      harga_rupiah: 0,
      ppn_type: 'include',
      ppn_percentage: 11,
      harga_sebelum_ppn: 0
    }
  ]);
  const [editKursUsd, setEditKursUsd] = useState(16000);
  const [editKursEur, setEditKursEur] = useState(17000);
  const [newSupplierId, setNewSupplierId] = useState('');
  const [newNamaSales, setNewNamaSales] = useState('');
  const [newNamaPerusahaan, setNewNamaPerusahaan] = useState('');
  const [newSupplierPhone, setNewSupplierPhone] = useState('');
  const [newSupplierAddress, setNewSupplierAddress] = useState('');
  const [newNpwp, setNewNpwp] = useState('');
  const [newBank, setNewBank] = useState('BCA');
  const [newNoRekening, setNewNoRekening] = useState('');
  const [newAtasNama, setNewAtasNama] = useState('');
  const [newMetodePembayaran, setNewMetodePembayaran] = useState('Cash');
  const [newJatuhTempo, setNewJatuhTempo] = useState('');
  const [newOngkirMode, setNewOngkirMode] = useState('transfer');
  const [newOngkir, setNewOngkir] = useState('');
  const [newCatatan, setNewCatatan] = useState('');

  // 1. Fetch Requests
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date')
  });

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    selectedCount
  } = useBatchSelection(requests, req => req.id);

  // Fetch purchasing tickets for linking
  const { data: tickets = [] } = useQuery({
    queryKey: ['purchasing-tickets'],
    queryFn: () => base44.entities.PurchasingTicket.list()
  });

  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: barangMasukList = [] } = useQuery({ queryKey: ['barang-masuk'], queryFn: () => base44.entities.BarangMasuk.list() });

  const { data: suppliers = [] } = useQuery({ 
    queryKey: ['supplier-database'], 
    queryFn: async () => {
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  const { data: productionExpenses = [] } = useQuery({
    queryKey: ['production-expenses'],
    queryFn: () => base44.entities.ProductionExpense.list()
  });

  const expenseBankMap = useMemo(() => {
    const map = {};
    productionExpenses.forEach(exp => {
      const sourceId = exp.source_id || exp.sourceId;
      if (sourceId && exp.bankAccountId) {
        map[sourceId] = accounts?.[exp.bankAccountId]?.name || exp.bankAccountId;
      }
    });
    return map;
  }, [productionExpenses, accounts]);

  const getPayingBank = useCallback((req) => {
    if (!req) return 'Apa Saja Bisa';
    if (req.bank_pembayar) return req.bank_pembayar;
    if (req.bank_pembayar_id && accounts?.[req.bank_pembayar_id]) {
      return accounts[req.bank_pembayar_id].name;
    }
    if (expenseBankMap[req.id]) {
      return expenseBankMap[req.id];
    }
    return 'Apa Saja Bisa';
  }, [expenseBankMap, accounts]);

  const allInventoryItems = useMemo(() => [
    ...bahanCairList.map(i => ({ ...i, _type: 'Bahan Cair' })),
    ...botolList.map(i => ({ ...i, _type: 'Botol' })),
    ...tutupList.map(i => ({ ...i, _type: 'Tutup' })),
    ...sprayList.map(i => ({ ...i, _type: 'Spray' })),
    ...fullsetList.map(i => ({ ...i, _type: 'Fullset' })),
    ...itemTambahanList.map(i => ({ ...i, _type: 'Item Tambahan' }))
  ].sort((a, b) => (a.nama || '').localeCompare(b.nama || '')), [bahanCairList, botolList, tutupList, sprayList, fullsetList, itemTambahanList]);

  const processedArrivalRefs = React.useRef(new Set());

  // Fungsi proses kedatangan barang & penambahan stok riil ke gudang/database (Dilindungi dari penambahan ganda)
  const processArrivalAndAddStock = useCallback(async (request) => {
    if (!request) return;
    const refNumber = (request.po_number || request.id || '').trim();
    if (!refNumber) return;

    // Proteksi: Cek apakah sudah pernah tercatat di BarangMasuk atau sudah diproses di sesi ini
    const alreadyInBM = (barangMasukList || []).some(bm => 
      (bm.nomor_referensi && bm.nomor_referensi.trim().toLowerCase() === refNumber.toLowerCase())
    );

    if (alreadyInBM || processedArrivalRefs.current.has(refNumber.toLowerCase())) {
      return; // Sudah tercatat & stok sudah pernah ditambah, JANGAN tambah lagi!
    }

    // Tandai langsung agar tidak terpanggil ganda secara asinkron
    processedArrivalRefs.current.add(refNumber.toLowerCase());

    let items = [];
    try {
      items = typeof request.batch_items === 'string' ? JSON.parse(request.batch_items) : (request.batch_items || []);
    } catch(e) {}
    
    if (!items || items.length === 0) {
      items = [{
        id: request.material_id,
        type: request.kategori === 'Bahan Cair' ? 'bahan-cair' : request.kategori?.toLowerCase(),
        nama_barang: request.nama_item || 'Item Purchasing',
        qty: parseFloat(String(request.qty_beli || 1).replace(',', '.')),
        satuan: request.satuan || (request.kategori === 'Bahan Cair' ? 'kg' : 'pcs'),
        harga_satuan: request.harga_unit || 0
      }];
    }

    // 1. Tambah stok riil di database inventori (HANYA SEKALI)
    for (const item of items) {
      const qtyToAdd = parseFloat(String(item.qty || item.jumlah || 1).replace(',', '.')) || 0;
      const itemName = (item.name || item.nama || item.nama_barang || request.nama_item || '').trim().toLowerCase();
      const itemType = item.type || (request.kategori === 'Bahan Cair' ? 'bahan-cair' : request.kategori?.toLowerCase());
      const itemId = item.id || request.material_id;

      if (itemType === 'bahan-cair' || request.kategori === 'Bahan Cair' || !itemType) {
        const match = bahanCairList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        if (match) {
          const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
          const newStok = Number((curStok + qtyToAdd).toFixed(2));
          await base44.entities.BahanCair.update(match.id, { stok: newStok });
        }
      } else if (itemType === 'botol') {
        const match = botolList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        if (match) {
          const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
          await base44.entities.Botol.update(match.id, { stok: curStok + qtyToAdd });
        }
      } else if (itemType === 'tutup') {
        const match = tutupList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        if (match) {
          const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
          await base44.entities.Tutup.update(match.id, { stok: curStok + qtyToAdd });
        }
      } else if (itemType === 'spray') {
        const match = sprayList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        if (match) {
          const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
          await base44.entities.Spray.update(match.id, { stok: curStok + qtyToAdd });
        }
      } else if (itemType === 'fullset') {
        const match = fullsetList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        if (match) {
          const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
          await base44.entities.Fullset.update(match.id, { stok: curStok + qtyToAdd });
        }
      } else if (itemType === 'item-tambahan') {
        const match = itemTambahanList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        if (match) {
          const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
          await base44.entities.ItemTambahan.update(match.id, { stok: curStok + qtyToAdd });
        }
      }
    }

    // 2. Buat transaksi fisik BarangMasuk di database jika belum ada
    const bmItems = items.map(it => ({
      nama_barang: it.name || it.nama || it.nama_barang || request.nama_item,
      jumlah: parseFloat(String(it.qty || it.jumlah || 1).replace(',', '.')),
      satuan: it.satuan || (request.kategori === 'Bahan Cair' ? 'kg' : 'pcs'),
      kategori: request.kategori || it.kategori || 'Bahan',
      harga_satuan: it.harga_rupiah || it.harga_beli || request.harga_unit || 0
    }));

    await base44.entities.BarangMasuk.create({
      tanggal: new Date().toISOString().split('T')[0],
      nomor_referensi: refNumber,
      supplier: request.nama_perusahaan || request.vendor || request.supplier || 'Supplier',
      ekspedisi: request.ekspedisi || 'Ekspedisi Vendor / Langsung',
      ongkir: request.ongkir || 0,
      ongkir_dibayar_oleh: 'Perusahaan',
      catatan: request.catatan || `Barang Masuk via PO ${refNumber}`,
      pic: request.pic || member?.full_name || 'Purchasing Staff',
      input_by: member?.email || 'System',
      items: bmItems
    });

    // 3. Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['barang-masuk'] });
    queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
    queryClient.invalidateQueries({ queryKey: ['bahan-cair'] });
    queryClient.invalidateQueries({ queryKey: ['botol'] });
    queryClient.invalidateQueries({ queryKey: ['tutup'] });
    queryClient.invalidateQueries({ queryKey: ['spray'] });
    queryClient.invalidateQueries({ queryKey: ['fullset'] });
    queryClient.invalidateQueries({ queryKey: ['item-tambahan'] });
  }, [bahanCairList, botolList, tutupList, sprayList, fullsetList, itemTambahanList, barangMasukList, member, queryClient]);



  // 2. Mutations
  const createRequestMut = useMutation({
    mutationFn: (data) => base44.entities.PurchasingRequest.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
      toast.success('Pengajuan pembelian berhasil dibuat!');
      setIsAddOpen(false);
      resetForm();
    },
    onError: (err) => {
      toast.error('Gagal membuat pengajuan: ' + err.message);
    }
  });

  const updateRequestMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PurchasingRequest.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
    },
    onError: (err) => {
      toast.error('Gagal memperbarui status: ' + err.message);
    }
  });

  const updateTicketMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PurchasingTicket.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
    }
  });

  const batchDeleteRequest = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.PurchasingRequest.delete(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
      toast.success('Pengajuan berhasil dihapus!');
      clearSelection();
    },
    onError: (err) => {
      toast.error('Gagal menghapus pengajuan: ' + err.message);
    }
  });

  const resetForm = () => {
    setEditingRequestId(null);
    setEditItems([
      {
        id: 'item_1',
        inventory_key: '',
        type: 'bahan-cair',
        name: '',
        qty: 1,
        satuan: 'kg',
        mata_uang: 'IDR',
        harga_dollar: null,
        harga_euro: null,
        harga_rupiah: 0,
        ppn_type: 'include',
        ppn_percentage: 11,
        harga_sebelum_ppn: 0
      }
    ]);
    setEditKursUsd(kurs || 16000);
    setEditKursEur(kursEuro || 17000);
    setNewSupplierId('');
    setNewNamaSales('');
    setNewNamaPerusahaan('');
    setNewSupplierPhone('');
    setNewSupplierAddress('');
    setNewNpwp('');
    setNewBank('BCA');
    setNewNoRekening('');
    setNewAtasNama('');
    setNewMetodePembayaran('Cash');
    setNewJatuhTempo('');
    setNewOngkirMode('transfer');
    setNewOngkir('');
    setNewCatatan('');
  };

  const handleAddItem = () => {
    setEditItems(prev => [
      ...prev,
      {
        id: `item_${Date.now()}_${prev.length + 1}`,
        inventory_key: '',
        type: 'bahan-cair',
        name: '',
        qty: 1,
        satuan: 'kg',
        mata_uang: 'IDR',
        harga_dollar: null,
        harga_euro: null,
        harga_rupiah: 0,
        ppn_type: 'include',
        ppn_percentage: 11,
        harga_sebelum_ppn: 0
      }
    ]);
  };

  const handleRemoveItem = (idx) => {
    if (editItems.length <= 1) {
      toast.error('Pengajuan harus memiliki minimal 1 item.');
      return;
    }
    setEditItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateItem = (idx, field, value) => {
    setEditItems(prev => {
      const next = [...prev];
      const cur = { ...next[idx], [field]: value };
      
      const rateUsd = parseRate(editKursUsd, kurs || 16000);
      const rateEur = parseRate(editKursEur, kursEuro || 17000);

      if (field === 'name') {
        const match = allInventoryItems.find(inv => (inv.nama || '').trim().toLowerCase() === (value || '').trim().toLowerCase());
        if (match) {
          cur.inventory_key = `${match._type}_id_${match.id}`;
          if (!cur.satuan) cur.satuan = match.satuan || (match._type === 'Bahan Cair' ? 'kg' : 'pcs');
        }
      }

      if (field === 'harga_dollar') {
        const dVal = parseFloat(value) || 0;
        cur.harga_rupiah = dVal * rateUsd;
      } else if (field === 'harga_euro') {
        const eVal = parseFloat(value) || 0;
        cur.harga_rupiah = eVal * rateEur;
      } else if (field === 'harga_rupiah') {
        const rVal = parseFloat(value) || 0;
        cur.harga_rupiah = rVal;
        cur.harga_sebelum_ppn = rVal;
      } else if (field === 'mata_uang') {
        cur.mata_uang = value;
        if (value === 'IDR') {
          if (!cur.harga_rupiah && cur.harga_dollar) {
            cur.harga_rupiah = (parseFloat(cur.harga_dollar) || 0) * rateUsd;
          } else if (!cur.harga_rupiah && cur.harga_euro) {
            cur.harga_rupiah = (parseFloat(cur.harga_euro) || 0) * rateEur;
          }
          cur.harga_dollar = null;
          cur.harga_euro = null;
        } else if (value === 'USD') {
          if (!cur.harga_dollar && cur.harga_rupiah) {
            cur.harga_dollar = (parseFloat(cur.harga_rupiah) || 0) / rateUsd;
          }
          cur.harga_euro = null;
        } else if (value === 'EUR') {
          if (!cur.harga_euro && cur.harga_rupiah) {
            cur.harga_euro = (parseFloat(cur.harga_rupiah) || 0) / rateEur;
          }
          cur.harga_dollar = null;
        }
      }
      
      next[idx] = cur;
      return next;
    });
  };

  const handleSelectInventoryItem = (idx, val) => {
    if (!val) {
      setEditItems(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], inventory_key: '' };
        return next;
      });
      return;
    }
    const [type, id] = val.split('_id_');
    const item = allInventoryItems.find(i => String(i.id) === id && i._type === type);
    if (item) {
      const isUsd = item.mata_uang === 'USD';
      const isEur = item.mata_uang === 'EUR';
      const activeRate = isEur ? parseRate(editKursEur, kursEuro || 17000) : parseRate(editKursUsd, kurs || 16000);
      
      let priceRupiah = parseFloat(item.harga_rupiah || item.harga_beli || 0);
      let priceDollar = null;
      let priceEuro = null;
      let currency = 'IDR';

      if (isUsd && item.harga_dollar != null && parseFloat(item.harga_dollar) > 0) {
        currency = 'USD';
        priceDollar = parseFloat(item.harga_dollar);
        priceRupiah = priceDollar * activeRate;
      } else if (isEur && item.harga_euro != null && parseFloat(item.harga_euro) > 0) {
        currency = 'EUR';
        priceEuro = parseFloat(item.harga_euro);
        priceRupiah = priceEuro * activeRate;
      } else {
        currency = 'IDR';
        priceRupiah = parseFloat(item.harga_rupiah || item.harga_beli || 0);
        priceDollar = null;
        priceEuro = null;
      }

      setEditItems(prev => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          id: item.id,
          inventory_key: val,
          type: item._type === 'Bahan Cair' ? 'bahan-cair' : item._type.toLowerCase(),
          name: item.nama,
          satuan: item.satuan || (item._type === 'Bahan Cair' ? 'kg' : 'pcs'),
          mata_uang: currency,
          harga_dollar: priceDollar,
          harga_euro: priceEuro,
          harga_rupiah: priceRupiah,
          ppn_type: item.ppn_type || 'include',
          ppn_percentage: item.ppn_percentage || 11,
          harga_sebelum_ppn: item.harga_sebelum_ppn || priceRupiah
        };
        return next;
      });
    }
  };

  // Realtime totals computed for the edit modal
  const editModalTotals = useMemo(() => {
    let subtotalBarang = 0;
    let subtotalPpn = 0;
    let totalValasUsd = 0;
    let totalValasEur = 0;
    let hasValasUsd = false;
    let hasValasEur = false;

    const rateUsd = parseRate(editKursUsd, kurs || 16000);
    const rateEur = parseRate(editKursEur, kursEuro || 17000);

    editItems.forEach(item => {
      const qty = parseFloat(item.qty) || 0;
      const isUsd = item.mata_uang === 'USD';
      const isEur = item.mata_uang === 'EUR';
      
      let unitPriceRupiah = 0;
      if (isEur) {
        hasValasEur = true;
        const val = parseFloat(item.harga_euro) || 0;
        totalValasEur += qty * val;
        unitPriceRupiah = val * rateEur;
      } else if (isUsd) {
        hasValasUsd = true;
        const val = parseFloat(item.harga_dollar) || 0;
        totalValasUsd += qty * val;
        unitPriceRupiah = val * rateUsd;
      } else {
        unitPriceRupiah = parseFloat(item.harga_rupiah) || 0;
      }

      let baseTotal = 0;
      let ppnAmt = 0;

      if (item.ppn_type === 'exclude') {
        baseTotal = qty * unitPriceRupiah;
        const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
        ppnAmt = baseTotal * (pct / 100);
      } else {
        baseTotal = qty * unitPriceRupiah;
        ppnAmt = 0;
      }

      subtotalBarang += baseTotal;
      subtotalPpn += ppnAmt;
    });

    const ongkirVal = newOngkirMode === 'free_ongkir' ? 0 : (parseFloat(newOngkir) || 0);
    const hasExcludePpn = editItems.some(it => it.ppn_type === 'exclude');
    const hasIncludePpn = editItems.some(it => it.ppn_type === 'include');
    const ppnPct = hasExcludePpn ? (editItems.find(it => it.ppn_type === 'exclude')?.ppn_percentage || 11) : 11;
    const totalTransfer = newOngkirMode === 'transfer' ? (subtotalBarang + subtotalPpn + ongkirVal) : (subtotalBarang + subtotalPpn);

    return {
      subtotalBarang,
      subtotalPpn,
      ongkirVal,
      totalTransfer,
      totalValasUsd,
      totalValasEur,
      hasValasUsd,
      hasValasEur,
      hasExcludePpn,
      hasIncludePpn,
      ppnPct
    };
  }, [editItems, editKursUsd, editKursEur, newOngkirMode, newOngkir, kurs, kursEuro]);

  const handleCreateRequest = (e) => {
    if (e) e.preventDefault();
    if (!newNamaSales.trim() || !newNoRekening.trim() || !newAtasNama.trim()) {
      toast.error('Mohon lengkapi nama sales, nomor rekening, dan nama pemilik rekening!');
      return;
    }
    if (newMetodePembayaran === 'Tempo' && !newJatuhTempo) {
      toast.error('Tanggal jatuh tempo wajib diisi untuk metode Tempo!');
      return;
    }
    if (editItems.length === 0 || editItems.some(it => !it.name || !it.name.trim() || !(parseFloat(it.qty) > 0))) {
      toast.error('Mohon isi nama barang dan kuantiti yang valid untuk semua item!');
      return;
    }

    const rateUsd = parseRate(editKursUsd, kurs || 16000);
    const rateEur = parseRate(editKursEur, kursEuro || 17000);

    let totalBarang = 0;
    let totalPpn = 0;
    const preparedBatchItems = [];

    for (const item of editItems) {
      const buyQty = parseFloat(item.qty) || 0;
      const valasUnit = item.mata_uang === 'EUR' ? parseFloat(item.harga_euro) : (item.mata_uang === 'USD' ? parseFloat(item.harga_dollar) : null);
      const currentRate = item.mata_uang === 'EUR' ? rateEur : rateUsd;
      const unitPriceRupiah = valasUnit ? (valasUnit * currentRate) : parseFloat(item.harga_rupiah || 0);

      let baseTotal = 0;
      let ppnAmt = 0;

      if (item.ppn_type === 'exclude') {
        baseTotal = buyQty * unitPriceRupiah;
        const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
        ppnAmt = baseTotal * (pct / 100);
      } else {
        baseTotal = buyQty * unitPriceRupiah;
        ppnAmt = 0;
      }

      totalBarang += baseTotal;
      totalPpn += ppnAmt;

      preparedBatchItems.push({
        id: item.id,
        inventory_item_id: item.id,
        inventory_key: item.inventory_key || null,
        type: item.type,
        name: item.name.trim(),
        qty: buyQty,
        ticket: item.ticket || null,
        harga_rupiah: unitPriceRupiah,
        mata_uang: item.mata_uang || (valasUnit ? (item.mata_uang === 'EUR' ? 'EUR' : 'USD') : 'IDR'),
        harga_dollar: valasUnit && item.mata_uang !== 'EUR' ? valasUnit : (item.harga_dollar || null),
        harga_euro: valasUnit && item.mata_uang === 'EUR' ? valasUnit : (item.harga_euro || null),
        satuan: item.satuan || 'pcs',
        ppn_type: item.ppn_type || 'include',
        ppn_percentage: item.ppn_percentage || 11,
        harga_sebelum_ppn: item.ppn_type === 'exclude' ? (valasUnit ? valasUnit * currentRate : (item.harga_sebelum_ppn || unitPriceRupiah)) : unitPriceRupiah
      });
    }

    const ongkirVal = newOngkirMode === 'free_ongkir' ? 0 : (parseFloat(newOngkir) || 0);
    const totalTransfer = (newOngkirMode === 'transfer' ? totalBarang + totalPpn + ongkirVal : totalBarang + totalPpn);
    const isBatch = preparedBatchItems.length > 1;

    const hasExcludePpn = preparedBatchItems.some(it => it.ppn_type === 'exclude');
    const ppnPctDisplay = hasExcludePpn ? (preparedBatchItems.find(it => it.ppn_type === 'exclude')?.ppn_percentage || 11) : 0;
    const hasUsd = preparedBatchItems.some(it => it.mata_uang === 'USD' || it.harga_dollar);
    const hasEur = preparedBatchItems.some(it => it.mata_uang === 'EUR' || it.harga_euro);

    const generatedPoNumber = editingRequestId 
      ? requests.find(r => r.id === editingRequestId)?.po_number 
      : generatePONumber({ buyerName: 'PT SATALA GROUP', salesName: newNamaSales, existingRequests: requests });

    const reqData = {
      nama_item: isBatch ? `Batch Order: ${preparedBatchItems.length} Items (Multivendor)` : preparedBatchItems[0].name,
      kategori: isBatch ? 'Batch Request' : (preparedBatchItems[0].type === 'bahan-cair' ? 'Bahan Cair' : preparedBatchItems[0].type),
      stok_gudang: isBatch ? 0 : (parseFloat(preparedBatchItems[0].stok || 0)),
      kebutuhan: isBatch ? 0 : (parseFloat(preparedBatchItems[0].kebutuhan || 0)),
      qty_beli: isBatch ? preparedBatchItems.length : preparedBatchItems[0].qty,
      harga_unit: isBatch ? 0 : preparedBatchItems[0].harga_rupiah,
      harga_dollar: isBatch ? null : preparedBatchItems[0].harga_dollar,
      harga_euro: isBatch ? null : preparedBatchItems[0].harga_euro,
      mata_uang: isBatch ? null : preparedBatchItems[0].mata_uang,
      satuan: isBatch ? 'Item' : (preparedBatchItems[0].satuan || 'pcs'),
      total_biaya: totalTransfer,
      total_barang: totalBarang,
      ongkir: ongkirVal,
      ongkir_mode: newOngkirMode,
      ppn_status: hasExcludePpn ? 'exclude' : 'include',
      ppn_percentage: hasExcludePpn ? ppnPctDisplay : null,
      ppn_nominal: totalPpn,
      bank_tujuan: newBank,
      nomor_rekening: newNoRekening.trim(),
      atas_nama: newAtasNama.trim(),
      catatan: newCatatan.trim(),
      nama_sales: newNamaSales.trim(),
      nama_perusahaan: newNamaPerusahaan.trim(),
      npwp: newNpwp.trim(),
      supplier_phone: newSupplierPhone.trim(),
      supplier_address: newSupplierAddress.trim(),
      po_number: generatedPoNumber,
      kurs_usd: hasUsd ? rateUsd : null,
      kurs_eur: hasEur ? rateEur : null,
      metode_pembayaran: newMetodePembayaran,
      tanggal_jatuh_tempo: newMetodePembayaran === 'Tempo' ? newJatuhTempo : null,
      material_id: isBatch ? null : preparedBatchItems[0].id,
      material_type: isBatch ? null : preparedBatchItems[0].type,
      batch_items: JSON.stringify(preparedBatchItems),
      pic: member?.full_name || member?.email || 'Purchasing Staff',
      ...( !editingRequestId && {
        status: newMetodePembayaran === 'Tempo' ? 'Dalam Pengiriman' : 'Menunggu Pembayaran',
        status_pembayaran_tempo: newMetodePembayaran === 'Tempo' ? 'Menunggu Jatuh Tempo' : null,
        created_date: new Date().toISOString()
      })
    };

    if (editingRequestId) {
      updateRequestMut.mutate({ id: editingRequestId, data: reqData }, {
        onSuccess: () => {
          toast.success('Pengajuan pembelian berhasil diperbarui!');
          setIsAddOpen(false);
          resetForm();
        }
      });
    } else {
      createRequestMut.mutate(reqData);
    }
  };

  const handleConfirmSplit = async () => {
    if (!selectedRequest || splitSelectedItems.size === 0) return;
    try {
      const originalItems = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : (selectedRequest.batch_items || []);
      const splitItemsArr = [];
      const remainItemsArr = [];

      originalItems.forEach(item => {
        const itemId = item.inventory_item_id || item.id || item.name || item.nama || item.nama_barang;
        if (splitSelectedItems.has(itemId)) {
          splitItemsArr.push(item);
        } else {
          remainItemsArr.push(item);
        }
      });

      if (remainItemsArr.length === 0) {
        toast.error('Tidak bisa memindahkan semua item. Gunakan fitur edit/hapus saja.');
        return;
      }

      // Calculate new totals for Original Request
      let remainTotalBarang = 0;
      let remainPpnNominal = 0;
      remainItemsArr.forEach(item => {
        const qty = parseFloat(item.qty || item.buyQty || item.qty_beli || 0);
        const harga = parseFloat(item.harga_rupiah || item.harga_beli || 0);
        const diskon = parseFloat(item.diskon_nominal || 0);
        const itemTotal = (qty * harga) - diskon;
        remainTotalBarang += itemTotal;
        
        if (item.ppn_type === 'exclude') {
          const pct = parseFloat(item.ppn_percentage || 11);
          remainPpnNominal += itemTotal * (pct / 100);
        }
      });
      const remainOngkir = selectedRequest.ongkir > 0 && selectedRequest.ongkir_mode !== 'petty_cash' ? selectedRequest.ongkir : 0;
      const remainTotalBiaya = remainTotalBarang + remainPpnNominal + remainOngkir;

      // Calculate new totals for Split Request
      let splitTotalBarang = 0;
      let splitPpnNominal = 0;
      splitItemsArr.forEach(item => {
        const qty = parseFloat(item.qty || item.buyQty || item.qty_beli || 0);
        const harga = parseFloat(item.harga_rupiah || item.harga_beli || 0);
        const diskon = parseFloat(item.diskon_nominal || 0);
        const itemTotal = (qty * harga) - diskon;
        splitTotalBarang += itemTotal;
        
        if (item.ppn_type === 'exclude') {
          const pct = parseFloat(item.ppn_percentage || 11);
          splitPpnNominal += itemTotal * (pct / 100);
        }
      });
      const splitTotalBiaya = splitTotalBarang + splitPpnNominal; // No ongkir for new invoice

      // Create new request
      const newPoNumber = selectedRequest.po_number ? `${selectedRequest.po_number}-B` : generatePONumber({ salesName: selectedRequest.nama_sales, existingRequests: requests });
      const splitReqData = {
        ...selectedRequest,
        id: undefined, // Let backend generate new ID
        po_number: newPoNumber,
        batch_items: JSON.stringify(splitItemsArr),
        total_barang: splitTotalBarang,
        ppn_nominal: splitPpnNominal,
        total_biaya: splitTotalBiaya,
        custom_total_biaya: splitTotalBiaya,
        ongkir: 0,
        qty_beli: splitItemsArr.length === 1 ? parseFloat(splitItemsArr[0].qty || splitItemsArr[0].buyQty || 1) : splitItemsArr.length,
        harga_unit: splitItemsArr.length === 1 ? parseFloat(splitItemsArr[0].harga_rupiah || splitItemsArr[0].harga_beli || 0) : 0,
        nama_item: splitItemsArr.length === 1 ? (splitItemsArr[0].name || splitItemsArr[0].nama || splitItemsArr[0].nama_barang) : (originalItems.length > 1 && !selectedRequest.nama_item.includes('Batch') ? `${selectedRequest.nama_item} (Pecahan)` : `Batch Order: ${splitItemsArr.length} Items (Pecahan)`),
        catatan: (selectedRequest.catatan ? selectedRequest.catatan + ' | ' : '') + 'Pecahan dari invoice asli'
      };
      delete splitReqData.created_date; // let it use default

      // Update original request
      const originalReqData = {
        batch_items: JSON.stringify(remainItemsArr),
        total_barang: remainTotalBarang,
        ppn_nominal: remainPpnNominal,
        total_biaya: remainTotalBiaya,
        custom_total_biaya: remainTotalBiaya,
        qty_beli: remainItemsArr.length === 1 ? parseFloat(remainItemsArr[0].qty || remainItemsArr[0].buyQty || 1) : remainItemsArr.length,
        harga_unit: remainItemsArr.length === 1 ? parseFloat(remainItemsArr[0].harga_rupiah || remainItemsArr[0].harga_beli || 0) : 0,
        nama_item: remainItemsArr.length === 1 ? (remainItemsArr[0].name || remainItemsArr[0].nama || remainItemsArr[0].nama_barang) : `Batch Order: ${remainItemsArr.length} Items (Multivendor)`
      };

      // Execute mutations
      await createRequestMut.mutateAsync(splitReqData);
      await updateRequestMut.mutateAsync({ id: selectedRequest.id, data: originalReqData });
      
      toast.success('Berhasil memecah invoice menjadi 2!');
      setIsSplitModalOpen(false);
      setSplitSelectedItems(new Set());
      setSelectedRequest(null);
    } catch (err) {
      toast.error('Terjadi kesalahan saat memecah invoice.');
    }
  };

  const handleOpenEdit = (req) => {
    setEditingRequestId(req.id);
    setNewNamaSales(req.nama_sales || '');
    setNewNamaPerusahaan(req.nama_perusahaan || '');
    setNewSupplierPhone(req.supplier_phone || '');
    setNewSupplierAddress(req.supplier_address || '');
    setNewNpwp(req.npwp || '');
    setNewBank(req.bank_tujuan || 'BCA');
    setNewNoRekening(req.nomor_rekening || '');
    setNewAtasNama(req.atas_nama || '');
    setNewMetodePembayaran(req.metode_pembayaran || 'Cash');
    setNewJatuhTempo(req.tanggal_jatuh_tempo || '');
    setNewOngkirMode(req.ongkir_mode || 'transfer');
    setNewOngkir(req.ongkir !== undefined && req.ongkir !== null ? String(req.ongkir) : '');
    setNewCatatan(req.catatan || '');
    
    // Kurs PO
    const reqKursUsd = parseRate(req.kurs_usd, kurs || 16000);
    const reqKursEur = parseRate(req.kurs_eur, kursEuro || 17000);
    setEditKursUsd(reqKursUsd);
    setEditKursEur(reqKursEur);

    // Supplier match
    const sup = suppliers.find(s => s.nama_sales === req.nama_sales || (req.nama_perusahaan && s.nama_perusahaan === req.nama_perusahaan));
    if (sup) setNewSupplierId(String(sup.id));
    else setNewSupplierId('');

    // Helper to find matching inventory item
    const findMatchingInventory = (itName, matId, invId, itId) => {
      if (!allInventoryItems || allInventoryItems.length === 0) return null;
      const nameClean = (itName || '').trim().toLowerCase();
      return allInventoryItems.find(inv => 
        (matId && String(inv.id) === String(matId)) ||
        (invId && String(inv.id) === String(invId)) ||
        (itId && String(inv.id) === String(itId)) ||
        (nameClean && (inv.nama || '').trim().toLowerCase() === nameClean)
      );
    };

    // Items
    let parsedItems = [];
    if (req.batch_items) {
      try {
        const raw = typeof req.batch_items === 'string' ? JSON.parse(req.batch_items) : req.batch_items;
        if (Array.isArray(raw) && raw.length > 0) {
          parsedItems = raw.map((it, idx) => {
            const itName = it.name || it.nama || it.nama_item || it.nama_barang || '';
            const matchingInv = findMatchingInventory(itName, it.material_id, it.inventory_item_id, it.id);
            const invKey = matchingInv ? `${matchingInv._type}_id_${matchingInv.id}` : '';
            const rawMataUang = it.mata_uang || req.mata_uang || (matchingInv?.mata_uang);
            const mataUang = (rawMataUang === 'USD' || rawMataUang === 'EUR') ? rawMataUang : 'IDR';
            
            let hargaDollar = mataUang === 'USD' ? (it.harga_dollar != null ? it.harga_dollar : (req.harga_dollar || matchingInv?.harga_dollar || null)) : null;
            let hargaEuro = mataUang === 'EUR' ? (it.harga_euro != null ? it.harga_euro : (req.harga_euro || matchingInv?.harga_euro || null)) : null;
            let hargaRupiah = it.harga_rupiah != null ? parseFloat(it.harga_rupiah) : parseFloat(it.harga_unit || it.harga_beli || req.harga_unit || 0);

            if (mataUang === 'USD' && hargaDollar != null) {
              hargaRupiah = (parseFloat(hargaDollar) || 0) * reqKursUsd;
            } else if (mataUang === 'EUR' && hargaEuro != null) {
              hargaRupiah = (parseFloat(hargaEuro) || 0) * reqKursEur;
            } else if (mataUang === 'IDR' && (!hargaRupiah || hargaRupiah === 0) && matchingInv) {
              hargaRupiah = parseFloat(matchingInv.harga_rupiah || matchingInv.harga_beli || 0);
            }

            return {
              id: it.id || (matchingInv ? matchingInv.id : `item_${idx + 1}`),
              inventory_key: invKey,
              type: it.type || (matchingInv ? (matchingInv._type === 'Bahan Cair' ? 'bahan-cair' : matchingInv._type.toLowerCase()) : (it.kategori || req.kategori || 'bahan-cair')),
              name: itName || (matchingInv ? matchingInv.nama : ''),
              qty: it.qty != null ? it.qty : (it.buyQty != null ? it.buyQty : (it.qty_beli != null ? it.qty_beli : 1)),
              satuan: it.satuan || (matchingInv ? (matchingInv.satuan || (matchingInv._type === 'Bahan Cair' ? 'kg' : 'pcs')) : (req.satuan || 'pcs')),
              mata_uang: mataUang,
              harga_dollar: hargaDollar,
              harga_euro: hargaEuro,
              harga_rupiah: hargaRupiah,
              ppn_type: it.ppn_type || req.ppn_status || 'include',
              ppn_percentage: it.ppn_percentage != null ? it.ppn_percentage : (req.ppn_percentage || 11),
              harga_sebelum_ppn: it.harga_sebelum_ppn != null ? it.harga_sebelum_ppn : (hargaRupiah || 0),
              ticket: it.ticket || null
            };
          });
        }
      } catch (e) {
        console.error('Error parsing batch_items:', e);
      }
    }

    if (parsedItems.length === 0) {
      const itName = req.nama_item || '';
      const matchingInv = findMatchingInventory(itName, req.material_id, req.inventory_item_id, null);
      const invKey = matchingInv ? `${matchingInv._type}_id_${matchingInv.id}` : '';
      const rawMataUang = req.mata_uang || (matchingInv?.mata_uang);
      const mataUang = (rawMataUang === 'USD' || rawMataUang === 'EUR') ? rawMataUang : 'IDR';
      let hargaDollar = mataUang === 'USD' ? (req.harga_dollar || matchingInv?.harga_dollar || null) : null;
      let hargaEuro = mataUang === 'EUR' ? (req.harga_euro || matchingInv?.harga_euro || null) : null;
      let hargaRupiah = parseFloat(req.harga_unit || matchingInv?.harga_rupiah || matchingInv?.harga_beli || 0);

      if (mataUang === 'USD' && hargaDollar != null) {
        hargaRupiah = (parseFloat(hargaDollar) || 0) * reqKursUsd;
      } else if (mataUang === 'EUR' && hargaEuro != null) {
        hargaRupiah = (parseFloat(hargaEuro) || 0) * reqKursEur;
      }

      parsedItems = [{
        id: req.material_id || (matchingInv ? matchingInv.id : 'item_1'),
        inventory_key: invKey,
        type: req.material_type || (matchingInv ? (matchingInv._type === 'Bahan Cair' ? 'bahan-cair' : matchingInv._type.toLowerCase()) : (req.kategori || 'bahan-cair')),
        name: itName || (matchingInv ? matchingInv.nama : ''),
        qty: req.qty_beli || 1,
        satuan: req.satuan || (matchingInv ? (matchingInv.satuan || (matchingInv._type === 'Bahan Cair' ? 'kg' : 'pcs')) : 'pcs'),
        mata_uang: mataUang,
        harga_dollar: hargaDollar,
        harga_euro: hargaEuro,
        harga_rupiah: hargaRupiah,
        ppn_type: req.ppn_status || 'include',
        ppn_percentage: req.ppn_percentage || 11,
        harga_sebelum_ppn: req.harga_sebelum_ppn || hargaRupiah || 0,
        ticket: null
      }];
    }

    setEditItems(parsedItems);
    setIsAddOpen(true);
  };

  const handleOpenMutasiConfirm = () => {
    if (!selectedRequest) return;
    const freshRequest = requests.find(r => r.id === selectedRequest.id) || selectedRequest;
    setMutasiAmount(freshRequest.custom_total_biaya !== undefined ? freshRequest.custom_total_biaya : (freshRequest.total_biaya || 0));
    setMutasiAccount((freshRequest.ada_faktur_pajak === 'Ya') ? 'mandiri' : 'bca');
    setMutasiDate(new Date().toLocaleDateString('en-CA'));
    setIsEditingMutasi(false);
    setDiskonNama(freshRequest.diskon_nama_temp || '');
    setShowMutasiConfirm(true);
  };

  const handleFinalConfirm = async () => {
    if (!selectedRequest) return;
    const freshRequest = requests.find(r => r.id === selectedRequest.id) || selectedRequest;
    
    try {
      // === Map PurchasingRequest.kategori → ProductionExpense category/subCategory ===
      const kategoriMap = {
        'botol': { category: 'Packaging', subCategory: 'Botol' },
        'tutup': { category: 'Packaging', subCategory: 'Lainnya' },
        'spray': { category: 'Packaging', subCategory: 'Spray' },
        'bahan cair': { category: 'Bahan Cair', subCategory: 'Lainnya' },
        'item tambahan': { category: 'Item Tambahan', subCategory: 'Lainnya' },
        'legalitas': { category: 'Legalitas', subCategory: 'Lainnya' },
        'packaging': { category: 'Packaging', subCategory: 'Lainnya' },
      };
      const catKey = (freshRequest.kategori || '').toLowerCase();
      const mapped = kategoriMap[catKey] || { category: 'Lainnya', subCategory: 'Lainnya' };
      const mutasiKategori = freshRequest.kategori === 'Legalitas' ? 'Legalitas' : 'Bahan Baku';

      // Split costs
      const ongkirVal = Number(freshRequest.ongkir) || 0;
      const finalMutasiAmount = isEditingMutasi ? (Number(tempMutasiAmount) || 0) : (Number(mutasiAmount) || 0);
      
      // Item cost adalah total mutasi final yang diinput/diedit dikurangi ongkir
      const itemCost = finalMutasiAmount - ongkirVal;

      // 1. Create ProductionExpense (Biaya Produksi) - Harga dihitung dari mutasi agar valid
      const qtyVal = Number(freshRequest.qty_beli) || Number(freshRequest.qty) || 1;
      const hargaBeliVal = qtyVal > 0 ? (itemCost / qtyVal) : itemCost; // Update to use the correct edited price
      const newExp = await base44.entities.ProductionExpense.create({
        date: mutasiDate || new Date().toISOString().split('T')[0],
        category: mapped.category,
        subCategory: mapped.subCategory,
        bankAccountId: mutasiAccount,
        itemName: freshRequest.nama_item,
        qty: qtyVal,
        hargaBeli: hargaBeliVal, 
        amount: itemCost, 
        description: `Pengajuan Pembelian: ${freshRequest.nama_item} (${freshRequest.kategori}) — PIC: ${freshRequest.pic}`,
        source: 'purchasing_request',
        source_id: freshRequest.id
      });

      // 2. Create Mutasi Keluar (Linked to ProductionExpense)
      await base44.entities.AccountMutation.create({
        date: mutasiDate || new Date().toISOString().split('T')[0],
        type: 'keluar',
        account: accounts?.[mutasiAccount]?.name || mutasiAccount,
        amount: itemCost,
        description: `[${mutasiKategori}] Biaya Produksi: ${mapped.category} - ${mapped.subCategory} (${freshRequest.nama_item})`,
        reference_id: `prodcost-${newExp.id}`
      });

      // 3. Create OperationalExpense (Biaya Operasional) - Khusus Ongkir & Mutasinya
      if (ongkirVal > 0) {
        const opexAccount = freshRequest.ongkir_mode === 'petty_cash' ? 'petty_cash' : mutasiAccount;
        const newOpex = await base44.entities.OperationalExpense.create({
           date: mutasiDate || new Date().toISOString().split('T')[0],
           category: 'Ongkir Pabrik',
           requester: 'Purchasing',
           bankAccountId: opexAccount,
           amount: ongkirVal,
           description: `Ongkir Pengajuan: ${freshRequest.nama_item} (${freshRequest.nama_sales || freshRequest.pic})`
        });

        // Mutasi untuk Ongkir (Kecuali Petty Cash jika Petty Cash tidak dilacak di Mutasi Utama)
        if (opexAccount !== 'petty_cash') {
          await base44.entities.AccountMutation.create({
            date: mutasiDate || new Date().toISOString().split('T')[0],
            type: 'keluar',
            account: accounts?.[opexAccount]?.name || opexAccount,
            amount: ongkirVal,
            description: `Biaya Operasional: Ongkir Pabrik - Ongkir Pengajuan: ${freshRequest.nama_item} (${freshRequest.pic})`,
            reference_id: `opex-${newOpex.id}`
          });
        }
      }

      const originalTotal = freshRequest.total_biaya || 0;
      const totalSudahTerbayar = (Number(freshRequest.jumlah_terbayar) || 0) + finalMutasiAmount;
      const isLunas = totalSudahTerbayar >= originalTotal;

      let newStatus = isLunas ? 'Sudah Dibayar' : 'Dicicil';
      let statusTempo = freshRequest.status_pembayaran_tempo;
      
      if (freshRequest.metode_pembayaran === 'Tempo') {
         statusTempo = isLunas ? 'Lunas' : 'Menunggu Jatuh Tempo';
         newStatus = isLunas ? 'Sudah Dibayar' : 'Dicicil';
      }

      // 4. Update PurchasingRequest status to Sudah Dibayar atau Dicicil
      await updateRequestMut.mutateAsync({
        id: freshRequest.id,
        data: { 
          status: newStatus,
          status_pembayaran_tempo: statusTempo,
          jumlah_terbayar: totalSudahTerbayar,
          total_barang: (freshRequest.total_barang || (freshRequest.qty_beli * freshRequest.harga_unit)),
          bank_pembayar: accounts?.[mutasiAccount]?.name || mutasiAccount,
          bank_pembayar_id: mutasiAccount,
          tanggal_bayar: mutasiDate || new Date().toISOString().split('T')[0],
          paid_at: new Date().toISOString()
        }
      });

      // 4. Find and update related PurchasingTicket(s) to "Dalam Pengiriman"
      if (freshRequest.batch_items) {
        let items = [];
        try { items = JSON.parse(selectedRequest.batch_items); } catch(e) {}
        for (const item of items) {
          const relatedTicket = tickets.find(t => 
            String(t.material_id) === String(item.id) && 
            (t.status === 'Dalam Permintaan' || t.status === 'Harus Dibeli')
          );
          if (relatedTicket) {
            await updateTicketMut.mutateAsync({ id: relatedTicket.id, data: { status: 'Dalam Pengiriman' } });
          }
        }
      } else if (selectedRequest.material_id) {
        const relatedTicket = tickets.find(t => 
          String(t.material_id) === String(selectedRequest.material_id) && 
          (t.status === 'Dalam Permintaan' || t.status === 'Harus Dibeli')
        );
        if (relatedTicket) {
          await updateTicketMut.mutateAsync({ id: relatedTicket.id, data: { status: 'Dalam Pengiriman' } });
        }
      }

      toast.success('Pembayaran berhasil! Tercatat di Mutasi Rekening & Biaya Produksi.');
      setSelectedRequest(prev => prev ? { ...prev, status: 'Sudah Dibayar' } : null);
      setShowMutasiConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      queryClient.invalidateQueries({ queryKey: ['production-expenses'] });
    } catch (err) {
      toast.error('Gagal mengkonfirmasi: ' + err.message);
    }
  };

  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Nomor rekening berhasil disalin!');
  };

  const searchedRequests = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    const cleanNumeric = term.replace(/[^0-9]/g, '');

    return requests.filter(req => {
      // 1. Date Range Filter (if dateFrom / dateTo are chosen)
      if (dateFrom || dateTo) {
        const reqDateStr = req.created_date || req.created_at || req.tanggal_pengajuan || req.date || '';
        let isoDate = '';
        if (/^\d{4}-\d{2}-\d{2}/.test(reqDateStr)) {
          isoDate = reqDateStr.substring(0, 10);
        } else if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}/.test(reqDateStr)) {
          const parts = reqDateStr.split(/[/-]/);
          isoDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        } else {
          const d = new Date(reqDateStr);
          if (!isNaN(d.getTime())) {
            isoDate = d.toISOString().substring(0, 10);
          }
        }
        if (dateFrom && isoDate && isoDate < dateFrom) return false;
        if (dateTo && isoDate && isoDate > dateTo) return false;
      }

      if (!term) return true;

      // 2. Text Search (Vendor, Item, Kategori, PO Number, Atas Nama, Bank, Rekening, Tanggal, Catatan)
      const vendorFields = `${req.nama_sales || ''} ${req.supplier || ''} ${req.nama_supplier || ''} ${req.atas_nama || ''} ${req.pic || ''}`;
      const itemFields = `${req.nama_item || ''} ${req.kategori || ''} ${req.catatan || ''} ${req.po_number || ''} ${req.id || ''}`;
      const bankFields = `${req.bank_tujuan || ''} ${req.nomor_rekening || ''}`;
      const dateDisplay = `${getRequestDateDisplay(req) || ''} ${getRequestDateOnly(req) || ''} ${req.created_date || ''} ${req.tanggal_pengajuan || ''} ${req.tanggal_jatuh_tempo || ''}`;

      // Batch items & Hutang items
      let subItemsText = '';
      if (req.batch_items) {
        try {
          const bItems = typeof req.batch_items === 'string' ? JSON.parse(req.batch_items) : req.batch_items;
          if (Array.isArray(bItems)) {
            subItemsText += ' ' + bItems.map(it => `${it.name || ''} ${it.nama || ''} ${it.nama_barang || ''} ${it.nama_item || ''} ${it.kategori || ''} ${it.deskripsi || ''} ${it.description || ''}`).join(' ');
          }
        } catch(e) {}
      }
      if (req.hutang_items) {
        try {
          const hItems = typeof req.hutang_items === 'string' ? JSON.parse(req.hutang_items) : req.hutang_items;
          if (Array.isArray(hItems)) {
            subItemsText += ' ' + hItems.map(it => `${it.nama_item || ''} ${it.nama || ''} ${it.name || ''} ${it.kategori || ''}`).join(' ');
          }
        } catch(e) {}
      }

      const fullText = `${vendorFields} ${itemFields} ${bankFields} ${dateDisplay} ${subItemsText}`.toLowerCase();
      if (fullText.includes(term)) return true;

      // 3. Nominal / Amount match
      if (cleanNumeric && cleanNumeric.length >= 2) {
        const amounts = [
          Math.round(Number(req.total_biaya) || 0),
          Math.round(Number(req.total_barang) || 0),
          Math.round(Number(req.total_transfer) || 0),
          Math.round(Number(req.harga_unit) || 0),
          Math.round(Number(req.sisa_tempo) || 0)
        ].filter(a => a > 0);

        for (const amt of amounts) {
          if (String(amt).includes(cleanNumeric) || amt.toLocaleString('id-ID').includes(term)) {
            return true;
          }
        }

        // Subitems nominals
        if (req.batch_items) {
          try {
            const bItems = typeof req.batch_items === 'string' ? JSON.parse(req.batch_items) : req.batch_items;
            if (Array.isArray(bItems)) {
              for (const it of bItems) {
                const sub = Math.round(Number(it.subtotal || it.total || it.harga || it.harga_unit) || 0);
                if (sub > 0 && String(sub).includes(cleanNumeric)) return true;
              }
            }
          } catch(e) {}
        }
        if (req.hutang_items) {
          try {
            const hItems = typeof req.hutang_items === 'string' ? JSON.parse(req.hutang_items) : req.hutang_items;
            if (Array.isArray(hItems)) {
              for (const it of hItems) {
                const sub = Math.round(Number(it.subtotal || it.total || it.harga || it.harga_unit) || 0);
                if (sub > 0 && String(sub).includes(cleanNumeric)) return true;
              }
            }
          } catch(e) {}
        }
      }

      return false;
    });
  }, [requests, searchTerm, dateFrom, dateTo]);

  const filteredRequests = useMemo(() => {
    return searchedRequests.filter(req => {
      let matchStatus = false;
      if (statusFilter === 'all') matchStatus = req.status !== 'Sudah Dibayar' || !!searchTerm;
      else if (statusFilter === 'Tempo') matchStatus = req.metode_pembayaran === 'Tempo' && req.status !== 'Sudah Dibayar' && req.status !== 'Ditolak' && req.status !== 'Menunggu Konfirmasi Tempo';
      else matchStatus = req.status === statusFilter;
      return matchStatus;
    }).map(req => {
      if (!req.nomor_rekening && req.nama_sales) {
        const sup = suppliers.find(s => s.nama_sales === req.nama_sales);
        if (sup) {
          return {
            ...req,
            bank_tujuan: sup.bank || '',
            nomor_rekening: sup.no_rekening || '',
            atas_nama: sup.atas_nama || ''
          };
        }
      }
      return req;
    });
  }, [searchedRequests, searchTerm, statusFilter, suppliers]);

  const countByStatus = useMemo(() => {
    const counts = { all: 0, 'Menunggu Pembayaran': 0, 'Dicicil': 0, 'Sudah Dibayar': 0, 'Tempo': 0, 'Menunggu Konfirmasi Tempo': 0, 'Ditolak': 0 };
    searchedRequests.forEach(r => { 
      if (counts[r.status] !== undefined) counts[r.status]++; 
      if (r.metode_pembayaran === 'Tempo' && r.status !== 'Sudah Dibayar' && r.status !== 'Ditolak' && r.status !== 'Menunggu Konfirmasi Tempo') counts['Tempo']++;
      if (r.status !== 'Sudah Dibayar' && r.status !== 'Ditolak') counts.all++;
    });
    return counts;
  }, [searchedRequests]);

  const searchSummary = useMemo(() => {
    if (!searchTerm.trim() && !dateFrom && !dateTo) return null;
    let totalNominal = 0;
    filteredRequests.forEach(r => {
      totalNominal += Number(r.total_biaya || r.total_barang || 0);
    });
    return {
      count: filteredRequests.length,
      totalNominal
    };
  }, [searchTerm, dateFrom, dateTo, filteredRequests]);

  const groupedRequests = useMemo(() => {
    const groups = {};
    filteredRequests.forEach(req => {
      const vendorName = req.nama_sales?.trim() || 'Tanpa Vendor / Lainnya';
      if (!groups[vendorName]) groups[vendorName] = [];
      groups[vendorName].push(req);
    });
    // Sort by supplier name
    return Object.entries(groups).sort((a, b) => {
      if (a[0] === 'Tanpa Vendor / Lainnya') return 1;
      if (b[0] === 'Tanpa Vendor / Lainnya') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [filteredRequests]);

  return (
    <>
    <div className="space-y-6 pb-16 print:hidden">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={requests.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} pengajuan terpilih?`)) {
            batchDeleteRequest.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteRequest.isPending}
      />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#E6D5B8]/30 pb-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-800">Pengajuan Pembelian</h1>
          <p className="text-xs text-slate-500 mt-1">
            {isFinance 
              ? 'Konfirmasi pembayaran untuk pengajuan pembelian dari tim Purchasing.' 
              : 'Daftar pengajuan dana untuk pembelian stok material & kelengkapan produksi.'}
          </p>
        </div>
        <div>
          <Dialog open={isAddOpen} onOpenChange={(open) => {
            setIsAddOpen(open);
            if (!open) setEditingRequestId(null);
          }}>
            {!isFinance && (
              <DialogTrigger asChild>
                <Button onClick={resetForm} className="bg-[#8C6208] hover:bg-[#6b4a06] text-white gap-2 font-bold px-6 py-5 rounded-xl shadow-md transition-all">
                  <Plus className="w-5 h-5" /> Buat Pengajuan
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-4xl bg-white rounded-3xl overflow-y-auto max-h-[92vh] p-6 border shadow-2xl">
              <DialogHeader className="border-b border-slate-100 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl font-black text-[#302621]">
                      {editingRequestId ? 'Edit Pengajuan Pembelian' : 'Form Pengajuan Pembelian'}
                    </DialogTitle>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {editingRequestId 
                        ? `Nomor PO: ${requests.find(r => r.id === editingRequestId)?.po_number || '-'} • Ubah detail item, harga, vendor, dan pembayaran` 
                        : 'Buat pengajuan dana pembelian material / batch item baru'}
                    </p>
                  </div>
                  {editingRequestId && (
                    <Badge className="bg-[#8C6208]/10 text-[#8C6208] border-[#8C6208]/30 font-bold text-xs px-3 py-1">
                      Mode Edit PO
                    </Badge>
                  )}
                </div>
              </DialogHeader>

              <form onSubmit={handleCreateRequest} className="space-y-6 pt-2">
                {/* ── SECTION 1: DATA VENDOR & REKENING PEMBAYARAN ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Card Vendor */}
                  <div className="p-4 rounded-2xl bg-slate-50/70 border border-slate-200/80 space-y-3">
                    <h4 className="text-xs font-black text-[#302621] uppercase tracking-wider flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-[#8C6208]" /> Informasi Vendor / Supplier
                    </h4>

                    <div>
                      <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Pilih dari Database Supplier</label>
                      <SearchableCombobox
                        value={newSupplierId}
                        onValueChange={(id) => {
                          setNewSupplierId(id);
                          if (!id) return;
                          const sup = suppliers.find(s => String(s.id) === String(id));
                          if (sup) {
                            setNewNamaSales(sup.nama_sales || '');
                            setNewNamaPerusahaan(sup.nama_perusahaan || '');
                            setNewSupplierPhone(sup.supplier_phone || sup.phone || sup.no_telp || '');
                            setNewSupplierAddress(sup.supplier_address || sup.address || sup.alamat || '');
                            if (sup.bank) setNewBank(sup.bank);
                            if (sup.no_rekening) setNewNoRekening(sup.no_rekening);
                            if (sup.atas_nama) setNewAtasNama(sup.atas_nama);
                            if (sup.metode_pembayaran) setNewMetodePembayaran(sup.metode_pembayaran);
                            if (sup.ongkir_mode) setNewOngkirMode(sup.ongkir_mode);
                            if (sup.npwp) setNewNpwp(sup.npwp);
                          }
                        }}
                        options={suppliers.map(s => ({
                          value: String(s.id),
                          label: `${s.nama_sales}${s.nama_perusahaan ? ` (${s.nama_perusahaan})` : ''} ${s.bank ? `[${s.bank} - ${s.atas_nama}]` : ''}`
                        }))}
                        placeholder="-- Pilih untuk auto-fill data vendor --"
                        searchPlaceholder="Cari vendor / sales..."
                        allowCustom={false}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Nama Sales / PIC <span className="text-rose-500">*</span></label>
                        <Input 
                          placeholder="Pak Budi / Sales" 
                          value={newNamaSales} 
                          onChange={(e) => setNewNamaSales(e.target.value)} 
                          required 
                          className="bg-white h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Nama Perusahaan</label>
                        <Input 
                          placeholder="PT. ABC Mandiri" 
                          value={newNamaPerusahaan} 
                          onChange={(e) => setNewNamaPerusahaan(e.target.value)} 
                          className="bg-white h-9 text-xs"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">No. Telepon</label>
                        <Input 
                          placeholder="08123456789" 
                          value={newSupplierPhone} 
                          onChange={(e) => setNewSupplierPhone(e.target.value)} 
                          className="bg-white h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">NPWP (Opsional)</label>
                        <Input 
                          placeholder="01.234.567.8-012.000" 
                          value={newNpwp} 
                          onChange={(e) => setNewNpwp(e.target.value)} 
                          className="bg-white h-9 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Pembayaran */}
                  <div className="p-4 rounded-2xl bg-slate-50/70 border border-slate-200/80 space-y-3">
                    <h4 className="text-xs font-black text-[#302621] uppercase tracking-wider flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-[#8C6208]" /> Rekening & Metode Pembayaran
                    </h4>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Bank <span className="text-rose-500">*</span></label>
                        <Select value={newBank} onValueChange={setNewBank}>
                          <SelectTrigger className="h-9 text-xs bg-white border-slate-200">
                            <SelectValue placeholder="Bank" />
                          </SelectTrigger>
                          <SelectContent className="z-[200]">
                            <SelectItem value="BCA">BCA</SelectItem>
                            <SelectItem value="Mandiri">Mandiri</SelectItem>
                            <SelectItem value="BRI">BRI</SelectItem>
                            <SelectItem value="BNI">BNI</SelectItem>
                            <SelectItem value="BSI">BSI</SelectItem>
                            <SelectItem value="CIMB">CIMB Niaga</SelectItem>
                            <SelectItem value="Permata">Permata</SelectItem>
                            <SelectItem value="Dana">Dana</SelectItem>
                            <SelectItem value="OVO">OVO</SelectItem>
                            <SelectItem value="GoPay">GoPay</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Nomor Rekening <span className="text-rose-500">*</span></label>
                        <Input 
                          placeholder="No. Rekening vendor" 
                          value={newNoRekening} 
                          onChange={(e) => setNewNoRekening(e.target.value)} 
                          required 
                          className="bg-white h-9 text-xs"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Atas Nama Rekening <span className="text-rose-500">*</span></label>
                      <Input 
                        placeholder="Nama pemilik rekening" 
                        value={newAtasNama} 
                        onChange={(e) => setNewAtasNama(e.target.value)} 
                        required 
                        className="bg-white h-9 text-xs"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/60">
                      <div>
                        <label className="text-[11px] font-bold text-[#7A695B] mb-1 block">Metode Pembayaran</label>
                        <Select value={newMetodePembayaran} onValueChange={setNewMetodePembayaran}>
                          <SelectTrigger className="h-9 text-xs bg-white border-slate-200 font-bold">
                            <SelectValue placeholder="Metode" />
                          </SelectTrigger>
                          <SelectContent className="z-[200]">
                            <SelectItem value="Cash">Cash (Bayar di Muka)</SelectItem>
                            <SelectItem value="Tempo">Tempo (Bayar Nanti)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newMetodePembayaran === 'Tempo' && (
                        <div>
                          <label className="text-[11px] font-bold text-orange-700 mb-1 block">Jatuh Tempo <span className="text-rose-500">*</span></label>
                          <CustomDatePicker value={newJatuhTempo} onChange={setNewJatuhTempo} required />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── SECTION 2: DAFTAR ITEM / MATERIAL (DYNAMIC MULTI-ITEM) ── */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                        <Package2 className="w-4 h-4 text-[#8C6208]" /> Daftar Item / Material ({editItems.length})
                      </h3>
                      <p className="text-[11px] text-slate-500">Edit item, kuantiti, harga satuan, dan mata uang secara terpisah</p>
                    </div>
                    <Button 
                      type="button" 
                      onClick={handleAddItem}
                      className="bg-[#8C6208] hover:bg-[#6b4a06] text-white text-xs font-bold gap-1.5 h-8 px-3.5 rounded-xl shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" /> Tambah Item
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {editItems.map((item, idx) => {
                      const isUsd = item.mata_uang === 'USD';
                      const isEur = item.mata_uang === 'EUR';
                      const rateUsd = parseRate(editKursUsd, kurs || 16000);
                      const rateEur = parseRate(editKursEur, kursEuro || 17000);
                      const activeRate = isEur ? rateEur : rateUsd;
                      const qty = parseFloat(item.qty) || 0;
                      const valasUnit = isEur ? (parseFloat(item.harga_euro) || 0) : (isUsd ? (parseFloat(item.harga_dollar) || 0) : 0);
                      const unitRupiah = (isUsd || isEur) ? (valasUnit * activeRate) : (parseFloat(item.harga_rupiah) || 0);
                      const itemSubtotal = qty * unitRupiah;

                      return (
                        <Card key={item.id || idx} className="border border-slate-200 bg-white rounded-2xl overflow-hidden shadow-xs">
                          <CardContent className="p-4 space-y-3">
                            {/* Card Item Header */}
                            <div className="flex items-center justify-between gap-3 pb-2 border-b border-slate-100">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-[#8C6208]/10 text-[#8C6208] font-black text-xs flex items-center justify-center">
                                  #{idx + 1}
                                </span>
                                <span className="text-xs font-bold text-slate-700 truncate max-w-[200px] sm:max-w-xs">
                                  {item.name || 'Item Baru'}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 flex-1 max-w-sm justify-end">
                                <div className="w-48 sm:w-64">
                                  <SearchableCombobox
                                    value={item.inventory_key || ''}
                                    options={allInventoryItems.map(inv => ({
                                      value: `${inv._type}_id_${inv.id}`,
                                      label: `${inv.nama} (${inv._type})`
                                    }))}
                                    placeholder="🔍 Pilih Master Inventori..."
                                    searchPlaceholder="Ketik nama item..."
                                    onValueChange={(val) => handleSelectInventoryItem(idx, val)}
                                    allowCustom={false}
                                  />
                                </div>
                                {editItems.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveItem(idx)}
                                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                    title="Hapus item ini"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Row 1: Nama Item & Kategori */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="sm:col-span-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 block">Nama Item / Material <span className="text-rose-500">*</span></label>
                                <Input 
                                  placeholder="Contoh: Alkohol 96% / Botol Lotion 100ml" 
                                  value={item.name} 
                                  onChange={(e) => handleUpdateItem(idx, 'name', e.target.value)} 
                                  required 
                                  className="h-9 text-xs font-semibold"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 block">Kategori</label>
                                <Select value={item.type || 'bahan-cair'} onValueChange={(val) => handleUpdateItem(idx, 'type', val)}>
                                  <SelectTrigger className="h-9 text-xs bg-white border-slate-200">
                                    <SelectValue placeholder="Kategori" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[200]">
                                    <SelectItem value="bahan-cair">Bahan Cair</SelectItem>
                                    <SelectItem value="botol">Botol</SelectItem>
                                    <SelectItem value="tutup">Tutup</SelectItem>
                                    <SelectItem value="spray">Spray</SelectItem>
                                    <SelectItem value="fullset">Fullset</SelectItem>
                                    <SelectItem value="item-tambahan">Item Tambahan</SelectItem>
                                    <SelectItem value="packaging">Packaging</SelectItem>
                                    <SelectItem value="other">Lainnya</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Row 2: Qty, Satuan, Mata Uang, & Harga */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50/70 p-3 rounded-xl border border-slate-100">
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 block">Qty Beli <span className="text-rose-500">*</span></label>
                                <Input 
                                  type="number" 
                                  step="any"
                                  min="0"
                                  placeholder="1" 
                                  value={item.qty} 
                                  onChange={(e) => handleUpdateItem(idx, 'qty', e.target.value)} 
                                  required 
                                  className="h-9 text-xs font-bold bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 block">Satuan</label>
                                <Input 
                                  placeholder="kg / pcs / ml" 
                                  value={item.satuan || 'pcs'} 
                                  onChange={(e) => handleUpdateItem(idx, 'satuan', e.target.value)} 
                                  className="h-9 text-xs bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 block">Mata Uang</label>
                                <Select value={item.mata_uang || 'IDR'} onValueChange={(val) => handleUpdateItem(idx, 'mata_uang', val)}>
                                  <SelectTrigger className="h-9 text-xs bg-white border-slate-200 font-bold">
                                    <SelectValue placeholder="Mata Uang" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[200]">
                                    <SelectItem value="IDR">IDR (Rupiah)</SelectItem>
                                    <SelectItem value="USD">USD ($)</SelectItem>
                                    <SelectItem value="EUR">EUR (€)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 block">
                                  {isUsd ? 'Harga Dollar ($)' : isEur ? 'Harga Euro (€)' : 'Harga Satuan (Rp)'}
                                </label>
                                {isUsd ? (
                                  <div>
                                    <Input 
                                      type="number" 
                                      step="any"
                                      placeholder="38.00" 
                                      value={item.harga_dollar != null ? item.harga_dollar : ''} 
                                      onChange={(e) => handleUpdateItem(idx, 'harga_dollar', e.target.value)} 
                                      className="h-9 text-xs font-bold bg-white text-blue-700"
                                    />
                                    <span className="text-[9px] text-slate-400 mt-0.5 block truncate">
                                      ≈ Rp {fmt(unitRupiah)}
                                    </span>
                                  </div>
                                ) : isEur ? (
                                  <div>
                                    <Input 
                                      type="number" 
                                      step="any"
                                      placeholder="35.00" 
                                      value={item.harga_euro != null ? item.harga_euro : ''} 
                                      onChange={(e) => handleUpdateItem(idx, 'harga_euro', e.target.value)} 
                                      className="h-9 text-xs font-bold bg-white text-purple-700"
                                    />
                                    <span className="text-[9px] text-slate-400 mt-0.5 block truncate">
                                      ≈ Rp {fmt(unitRupiah)}
                                    </span>
                                  </div>
                                ) : (
                                  <Input 
                                    type="number" 
                                    step="any"
                                    placeholder="Harga unit" 
                                    value={item.harga_rupiah != null ? item.harga_rupiah : ''} 
                                    onChange={(e) => handleUpdateItem(idx, 'harga_rupiah', e.target.value)} 
                                    className="h-9 text-xs font-bold bg-white text-slate-800"
                                  />
                                )}
                              </div>
                            </div>

                            {/* Row 3: PPN Option & Subtotal */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Status PPN:</span>
                                <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg">
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateItem(idx, 'ppn_type', 'include')}
                                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${item.ppn_type !== 'exclude' ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
                                  >
                                    Include PPN
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateItem(idx, 'ppn_type', 'exclude')}
                                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${item.ppn_type === 'exclude' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
                                  >
                                    Exclude PPN
                                  </button>
                                </div>
                                {item.ppn_type === 'exclude' && (
                                  <div className="flex items-center gap-1">
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="100" 
                                      value={item.ppn_percentage || 11} 
                                      onChange={(e) => handleUpdateItem(idx, 'ppn_percentage', e.target.value)} 
                                      className="h-7 w-14 text-[10px] font-bold text-center"
                                    />
                                    <span className="text-[10px] text-slate-400 font-bold">%</span>
                                  </div>
                                )}
                              </div>

                              <div className="text-right ml-auto">
                                <span className="text-[10px] text-slate-400 font-bold uppercase mr-2">Subtotal:</span>
                                {(isUsd || isEur) && valasUnit > 0 && (
                                  <span className="text-xs font-bold text-blue-700 mr-1.5">
                                    {isEur ? '€' : '$'} {fmt(qty * valasUnit)} <span className="opacity-60 text-slate-400">≈</span>
                                  </span>
                                )}
                                <span className="text-xs font-black text-slate-800">
                                  Rp {fmt(itemSubtotal)}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                {/* ── SECTION 3: KURS VALAS PO (JIKA ADA ITEM USD/EUR) ── */}
                {(editModalTotals.hasValasUsd || editModalTotals.hasValasEur) && (
                  <div className="p-4 bg-blue-50/70 border border-blue-200/80 rounded-2xl space-y-3">
                    <h4 className="text-xs font-black text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5 text-blue-700" /> Pengaturan Kurs PO (Rate Terkunci)
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {editModalTotals.hasValasUsd && (
                        <div>
                          <label className="text-[11px] font-bold text-blue-800 mb-1 block">Kurs USD / Dolar (Rp)</label>
                          <Input 
                            type="text" 
                            value={editKursUsd} 
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditKursUsd(val);
                              const parsed = parseRate(val, kurs || 16000);
                              setEditItems(prev => prev.map(it => {
                                if (it.mata_uang === 'USD' && it.harga_dollar != null) {
                                  const dVal = parseFloat(it.harga_dollar) || 0;
                                  return { ...it, harga_rupiah: dVal * parsed };
                                }
                                return it;
                              }));
                            }} 
                            className="bg-white border-blue-200 font-bold text-xs"
                          />
                          <span className="text-[9px] text-blue-600 mt-0.5 block">Kurs realtime saat ini: Rp {fmt(kurs)}</span>
                        </div>
                      )}
                      {editModalTotals.hasValasEur && (
                        <div>
                          <label className="text-[11px] font-bold text-blue-800 mb-1 block">Kurs EUR / Euro (Rp)</label>
                          <Input 
                            type="text" 
                            value={editKursEur} 
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditKursEur(val);
                              const parsed = parseRate(val, kursEuro || 17000);
                              setEditItems(prev => prev.map(it => {
                                if (it.mata_uang === 'EUR' && it.harga_euro != null) {
                                  const eVal = parseFloat(it.harga_euro) || 0;
                                  return { ...it, harga_rupiah: eVal * parsed };
                                }
                                return it;
                              }));
                            }} 
                            className="bg-white border-blue-200 font-bold text-xs"
                          />
                          <span className="text-[9px] text-blue-600 mt-0.5 block">Kurs realtime saat ini: Rp {fmt(kursEuro)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── SECTION 4: ONGKOS KIRIM & TOTAL TRANSFER ── */}
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-[#8C6208]" /> Ongkos Kirim
                  </h4>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setNewOngkirMode('transfer')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${newOngkirMode === 'transfer' ? 'border-[#8C6208] bg-[#8C6208]/5 text-[#8C6208]' : 'border-slate-200 text-slate-500'}`}>
                      <CreditCard className="w-4 h-4" /> Transfer
                    </button>
                    <button type="button" onClick={() => setNewOngkirMode('petty_cash')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${newOngkirMode === 'petty_cash' ? 'border-[#8C6208] bg-[#8C6208]/5 text-[#8C6208]' : 'border-slate-200 text-slate-500'}`}>
                      <Banknote className="w-4 h-4" /> Petty Cash
                    </button>
                    <button type="button" onClick={() => setNewOngkirMode('free_ongkir')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${newOngkirMode === 'free_ongkir' ? 'border-[#8C6208] bg-[#8C6208]/5 text-[#8C6208]' : 'border-slate-200 text-slate-500'}`}>
                      <Truck className="w-4 h-4" /> Free Ongkir
                    </button>
                  </div>
                  {newOngkirMode !== 'free_ongkir' && (
                    <div>
                      <label className="text-xs font-bold text-[#7A695B] mb-1 block">Biaya Ongkir (Rp)</label>
                      <Input type="number" placeholder="0" value={newOngkir} onChange={(e) => setNewOngkir(e.target.value)} />
                      {newOngkirMode === 'petty_cash' && <p className="text-[10px] text-amber-600 mt-1">💰 Petty cash: ongkir tidak ditambahkan ke total transfer</p>}
                    </div>
                  )}

                  {/* Ringkasan Biaya Realtime */}
                  <div className="bg-[#FAF7F2] border border-[#E6D5B8] rounded-2xl p-4 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600 font-medium">Subtotal Harga Barang:</span>
                      <div className="text-right">
                        {editModalTotals.totalValasUsd > 0 && (
                          <span className="text-xs font-extrabold text-blue-700 mr-2">$ {fmt(editModalTotals.totalValasUsd)}</span>
                        )}
                        {editModalTotals.totalValasEur > 0 && (
                          <span className="text-xs font-extrabold text-purple-700 mr-2">€ {fmt(editModalTotals.totalValasEur)}</span>
                        )}
                        <span className="font-bold text-slate-800">Rp {fmt(editModalTotals.subtotalBarang)}</span>
                      </div>
                    </div>

                    {editModalTotals.hasExcludePpn && editModalTotals.subtotalPpn > 0 && (
                      <div className="flex justify-between text-xs text-rose-700">
                        <span className="font-medium">Total PPN (Exclude {editModalTotals.ppnPct}%):</span>
                        <span className="font-bold">+ Rp {fmt(editModalTotals.subtotalPpn)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600 font-medium">Ongkos Kirim:</span>
                      <span className={`font-bold ${newOngkirMode === 'petty_cash' || newOngkirMode === 'free_ongkir' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                        Rp {fmt(editModalTotals.ongkirVal)}
                      </span>
                    </div>

                    <div className="border-t border-[#E6D5B8] pt-2.5 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-black text-slate-900 block">TOTAL TAGIHAN / TRANSFER:</span>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {newMetodePembayaran === 'Tempo' ? 'Metode Pembayaran: Tempo' : 'Metode Pembayaran: Cash'}
                        </span>
                      </div>
                      <span className="text-base font-black text-[#8C6208]">
                        Rp {fmt(editModalTotals.totalTransfer)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── SECTION 5: CATATAN PENGAJUAN ── */}
                <div>
                  <label className="text-xs font-bold text-[#7A695B] mb-1 block">Catatan / Keterangan Khusus</label>
                  <Input 
                    placeholder="Catatan untuk vendor atau tim finance..." 
                    value={newCatatan} 
                    onChange={(e) => setNewCatatan(e.target.value)} 
                  />
                </div>

                {/* ── ACTION BUTTONS ── */}
                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                  <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)} className="rounded-xl h-11 px-6">
                    Batal
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={createRequestMut.isPending || updateRequestMut.isPending} 
                    className="bg-[#8C6208] hover:bg-[#6b4a06] text-white font-bold rounded-xl h-11 px-8 gap-2 shadow-md"
                  >
                    <Save className="w-4 h-4" /> {editingRequestId ? 'Simpan Perubahan' : 'Buat Pengajuan'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="relative w-full md:flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Cari tanggal, nominal (Rp), vendor/sales, item yang dibeli, No PO..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-9 h-10 border-slate-200 focus-visible:ring-[#8C6208] text-xs sm:text-sm rounded-xl"
            />
            {searchTerm && (
              <button 
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                title="Hapus pencarian"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Dari:</span>
              <CustomDatePicker 
                value={dateFrom} 
                onChange={setDateFrom} 
                className="h-10 text-xs w-32 sm:w-36" 
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">S/d:</span>
              <CustomDatePicker 
                value={dateTo} 
                onChange={setDateTo} 
                className="h-10 text-xs w-32 sm:w-36" 
              />
            </div>
            {(searchTerm || dateFrom || dateTo || statusFilter !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setSearchTerm(''); setDateFrom(''); setDateTo(''); setStatusFilter('all'); }}
                className="text-xs h-10 text-slate-500 hover:text-slate-800 rounded-xl"
              >
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          <Button 
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            className={`flex-1 sm:flex-initial rounded-xl text-xs font-bold ${statusFilter === 'all' ? 'bg-[#302621] text-white hover:bg-black' : 'border-slate-200 text-slate-600'}`}
          >
            Semua ({countByStatus.all})
          </Button>
          <Button 
            variant={statusFilter === 'Menunggu Pembayaran' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Menunggu Pembayaran')}
            className={`flex-1 sm:flex-initial rounded-xl text-xs font-bold ${statusFilter === 'Menunggu Pembayaran' ? 'bg-[#302621] text-white hover:bg-black' : 'border-slate-200 text-slate-600'}`}
          >
            Belum Bayar ({countByStatus['Menunggu Pembayaran']})
          </Button>
          <Button 
            variant={statusFilter === 'Menunggu Konfirmasi Tempo' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Menunggu Konfirmasi Tempo')}
            className={`flex-1 sm:flex-initial rounded-xl text-xs font-bold ${statusFilter === 'Menunggu Konfirmasi Tempo' ? 'bg-[#302621] text-white hover:bg-black' : 'border-slate-200 text-slate-600'}`}
          >
            Konfirmasi Tempo ({countByStatus['Menunggu Konfirmasi Tempo']})
          </Button>
          <Button 
            variant={statusFilter === 'Dicicil' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Dicicil')}
            className={`flex-1 sm:flex-initial rounded-xl text-xs font-bold ${statusFilter === 'Dicicil' ? 'bg-[#302621] text-white hover:bg-black' : 'border-slate-200 text-slate-600'}`}
          >
            Dicicil ({countByStatus['Dicicil']})
          </Button>
          <Button 
            variant={statusFilter === 'Tempo' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Tempo')}
            className={`flex-1 sm:flex-initial rounded-xl text-xs font-bold ${statusFilter === 'Tempo' ? 'bg-[#302621] text-white hover:bg-black' : 'border-slate-200 text-slate-600'}`}
          >
            Tempo ({countByStatus['Tempo']})
          </Button>
          <Button 
            variant={statusFilter === 'Sudah Dibayar' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Sudah Dibayar')}
            className={`flex-1 sm:flex-initial rounded-xl text-xs font-bold ${statusFilter === 'Sudah Dibayar' ? 'bg-[#302621] text-white hover:bg-black' : 'border-slate-200 text-slate-600'}`}
          >
            Sudah Bayar ({countByStatus['Sudah Dibayar']})
          </Button>
        </div>

        {/* Results summary when filter/search is active */}
        {searchSummary && (
          <div className="pt-2 border-t border-amber-200/60 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-900 bg-amber-50/70 px-3.5 py-2.5 rounded-xl animate-in fade-in">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[#8C6208] shrink-0" />
              <span>
                Menampilkan <strong>{searchSummary.count}</strong> pengajuan pembelian
                {searchTerm ? <span> untuk <strong>"{searchTerm}"</strong></span> : null}
                {dateFrom || dateTo ? <span> periode <strong>{dateFrom || '...'} s/d {dateTo || '...'}</strong></span> : null}
              </span>
            </div>
            <div className="font-bold text-[#8C6208] ml-auto">
              Total Nominal: Rp {fmt(searchSummary.totalNominal)}
            </div>
          </div>
        )}
      </div>

      {/* Cards Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-3xl p-6 shadow-xs">
          <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto text-[#8C6208] mb-3">
            <Search className="w-6 h-6" />
          </div>
          <h3 className="text-slate-800 font-bold text-base mb-1">
            {searchTerm || dateFrom || dateTo ? `Tidak ditemukan pengajuan yang cocok` : 'Tidak ada pengajuan pembelian'}
          </h3>
          <p className="text-slate-500 text-xs max-w-md mx-auto mb-4">
            {searchTerm || dateFrom || dateTo 
              ? 'Coba periksa kata kunci nama item, nama vendor/sales, angka nominal, atau periksa tab status (Belum Bayar / Sudah Bayar).' 
              : (isFinance ? 'Belum ada pengajuan dari Purchasing.' : 'Gunakan tombol "Buat Pengajuan" atau ajukan dari halaman Item yang Harus Dibeli.')}
          </p>
          {(searchTerm || dateFrom || dateTo || statusFilter !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSearchTerm(''); setDateFrom(''); setDateTo(''); setStatusFilter('all'); }}
              className="text-xs font-bold border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 rounded-xl"
            >
              Reset Semua Filter & Pencarian
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {groupedRequests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(([vendorName, groupRequests]) => (
            <div key={vendorName} className="bg-white/40 p-4 sm:p-5 rounded-3xl border border-slate-200/60 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#302621] text-white rounded-xl flex items-center justify-center shadow-md">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-[#302621] uppercase tracking-tight">{vendorName}</h3>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{groupRequests.length} PENGAJUAN</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Pengajuan Group</p>
                  <p className="text-lg font-black text-[#8C6208]">Rp {fmt(groupRequests.reduce((s, r) => s + ((r.custom_total_biaya !== undefined ? r.custom_total_biaya : (r.total_biaya || 0)) - (Number(r.jumlah_terbayar) || 0)), 0))}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupRequests.map((req) => {
            let isUrgent = false;
            let diffDays = null;
            if (req.metode_pembayaran === 'Tempo' && req.tanggal_jatuh_tempo && req.status === 'Menunggu Pembayaran') {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const tempoDate = new Date(req.tanggal_jatuh_tempo);
              diffDays = Math.ceil((tempoDate - today) / (1000 * 60 * 60 * 24));
              isUrgent = diffDays <= 3;
            }

            return (
            <Card 
              key={req.id} 
              onClick={() => isSelectionMode && toggleSelection(req.id)}
              className={`border hover:border-[#8C6208]/40 hover:shadow-md transition-all overflow-hidden flex flex-col ${
                isSelectionMode ? 'cursor-pointer' : ''
              } ${
                selectedIds.has(req.id) 
                  ? 'border-rose-300 ring-1 ring-rose-300 bg-rose-50/20' 
                  : isUrgent 
                    ? 'border-red-500 bg-red-50 shadow-red-100/50' 
                    : 'bg-white border-slate-200/80'
              }`}
            >
              <CardContent className="p-0 flex flex-col h-full">
                {/* Header Status */}
                <div className={`px-4 py-2.5 flex justify-between items-center border-b ${
                  isUrgent 
                    ? 'bg-red-500 text-white border-red-600' 
                    : req.status === 'Menunggu Konfirmasi Tempo' ? 'bg-amber-100 border-amber-200' 
                    : req.status === 'Menunggu Pembayaran' ? 'bg-amber-50/50 border-slate-100' : 'bg-emerald-50/50 border-slate-100'
                }`}>
                  <div className="flex items-center gap-2">
                    {isSelectionMode && (
                      <Checkbox 
                        checked={selectedIds.has(req.id)} 
                        onCheckedChange={() => toggleSelection(req.id)}
                        onClick={e => e.stopPropagation()}
                      />
                    )}
                    <span className={`text-[10px] font-black uppercase tracking-wider ${isUrgent ? 'text-red-50' : 'text-slate-400'}`}>PIC: {req.pic}</span>
                  </div>
                  <Badge className={`text-[10px] font-black uppercase border tracking-wider shadow-none flex items-center gap-1.5 ${
                    isUrgent 
                      ? 'bg-white/20 text-white border-white/30'
                      : req.status === 'Menunggu Konfirmasi Tempo' ? 'bg-amber-500 text-white border-amber-600'
                      : req.status === 'Menunggu Pembayaran' ? 'bg-amber-100/50 text-amber-700 border-amber-200' : 'bg-emerald-100/60 text-emerald-800 border-emerald-300'
                  }`}>
                    <span>{req.status === 'Menunggu Pembayaran' && req.catatan?.includes('Hutang Manual') ? 'MENUNGGU KONFIRMASI FINANCE' : req.status}</span>
                    {req.status === 'Sudah Dibayar' && getPaymentDateDisplay(req) && (
                      <span className="font-bold text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded tracking-normal">
                        {getPaymentDateDisplay(req)}
                      </span>
                    )}
                  </Badge>
                </div>

                <div className="p-4 flex-1 flex flex-col">
                  {/* Title & Category */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      {req.po_number && (
                        <p className="text-[10px] font-black text-[#8C6208] tracking-wide">{req.po_number}</p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        {getRequestDateOnly(req) && (
                          <p className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                            <Clock className="w-3 h-3 text-slate-400" />
                            Masuk: {getRequestDateOnly(req)}
                          </p>
                        )}
                        {req.status === 'Sudah Dibayar' && getPaymentDateDisplay(req) && (
                          <p className="text-[10px] text-emerald-700 font-bold flex items-center gap-1 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            Dibayar: {getPaymentDateDisplay(req)}
                          </p>
                        )}
                      </div>
                    </div>
                    <h4 className="font-bold text-slate-800 text-base truncate flex items-center gap-2">
                      {req.nama_item}
                      {(req.po_number?.endsWith('-B') || req.nama_item?.includes('Pecahan') || req.catatan?.includes('Pecahan')) && (
                        <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200 py-0 h-4 px-1.5 flex-shrink-0">
                          PECAHAN INVOICE
                        </Badge>
                      )}
                    </h4>
                    {req.nama_sales && (
                      <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-1">
                        <User className="w-3.5 h-3.5" /> {req.nama_sales}
                      </p>
                    )}
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-[9px] uppercase bg-slate-50">{req.kategori}</Badge>
                      <Badge variant="outline" className="text-[9px] bg-slate-50">Est. Rp {fmt(req.harga_unit)} / unit</Badge>
                      {req.metode_pembayaran === 'Tempo' ? (
                        <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">
                          Tempo: {req.tanggal_jatuh_tempo}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          Cash
                        </Badge>
                      )}
                      {req.kurs_usd && (
                        <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                          Kurs USD: Rp {fmt(req.kurs_usd)}
                        </Badge>
                      )}
                      {req.kurs_eur && (
                        <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                          Kurs EUR: Rp {fmt(req.kurs_eur)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Stock Grid info */}
                  <div className="grid grid-cols-3 gap-2 text-center text-xs bg-slate-50/60 rounded-xl p-2.5 mb-4">
                    <div>
                      <p className="text-[8px] text-slate-400 font-bold uppercase">Stok Gudang</p>
                      <p className="font-bold text-slate-700">{req.stok_gudang || 0}</p>
                    </div>
                    <div>
                      <p className="text-[8px] text-slate-400 font-bold uppercase">Kebutuhan</p>
                      <p className="font-bold text-slate-700">{req.kebutuhan || 0}</p>
                    </div>
                    <div>
                      <p className="text-[8px] text-slate-400 font-bold uppercase">Beli Qty</p>
                      <p className="font-black text-[#8C6208]">{req.qty_beli || 0}</p>
                    </div>
                  </div>

                  {/* Bank info */}
                  <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-xl p-3 space-y-1.5 mb-4 bg-slate-50/40">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 font-bold text-slate-700">
                        <Landmark className="w-3.5 h-3.5 text-[#8C6208]" />
                        <span>{req.bank_tujuan}</span>
                      </div>
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Rekening Tujuan</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">No. Rekening:</span>
                      <span className="font-semibold text-slate-800">{req.nomor_rekening}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Atas Nama:</span>
                      <span className="font-semibold text-slate-800 truncate max-w-[150px]">{req.atas_nama}</span>
                    </div>

                    {/* Rekening Pembayar (Bank Kita) */}
                    {(req.status === 'Sudah Dibayar' || req.status === 'Dicicil') && (
                      <div className="mt-2 pt-2 border-t border-slate-200/80 flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-bold text-[11px] flex items-center gap-1">
                          <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
                          Dibayar Pakai:
                        </span>
                        <span className="font-black text-emerald-800 bg-emerald-100/90 border border-emerald-200 px-2 py-0.5 rounded-md text-[11px]">
                          {getPayingBank(req)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Ongkir Badge */}
                  {req.ongkir > 0 && (
                    <div className="flex items-center gap-2 text-xs mb-4">
                      <Badge variant="outline" className={`text-[9px] ${req.ongkir_mode === 'petty_cash' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                        {req.ongkir_mode === 'petty_cash' ? '💰 Petty Cash' : '💳 Transfer'} — Ongkir Rp {fmt(req.ongkir)}
                      </Badge>
                    </div>
                  )}

                    {/* Cost Breakdown Mini */}
                  <div className="text-[10px] text-slate-500 space-y-1 mb-3">
                    <div className="flex justify-between">
                      <span>Harga Barang</span>
                      <span className="font-semibold text-slate-600">Rp {fmt(req.total_barang || (req.qty_beli * req.harga_unit))}</span>
                    </div>
                    {req.ppn_nominal > 0 && (
                      <div className="flex justify-between text-amber-700">
                        <span className="font-semibold">PPN {req.ppn_percentage || 11}%</span>
                        <span className="font-bold">+ Rp {fmt(req.ppn_nominal)}</span>
                      </div>
                    )}
                    {req.ongkir > 0 && (
                      <div className="flex justify-between">
                        <span>Ongkir <span className="text-[8px]">({req.ongkir_mode === 'petty_cash' ? 'Petty Cash' : 'Transfer'})</span></span>
                        <span className={`font-semibold ${req.ongkir_mode === 'petty_cash' ? 'text-slate-400 line-through' : 'text-slate-600'}`}>+ Rp {fmt(req.ongkir)}</span>
                      </div>
                    )}
                    {(req.custom_total_biaya !== undefined && req.custom_total_biaya !== req.total_biaya) && (() => {
                      const selisih = req.total_biaya - req.custom_total_biaya;
                      const isDiskon = selisih > 0;
                      return (
                        <div className={`flex justify-between px-1.5 py-0.5 rounded mt-1 ${isDiskon ? 'text-rose-600 bg-rose-50' : 'text-amber-600 bg-amber-50'}`}>
                          <span>{req.diskon_nama_temp || (isDiskon ? 'Diskon Tambahan' : 'Biaya Tambahan')}</span>
                          <span className="font-bold">{isDiskon ? '-' : '+'} Rp {fmt(Math.abs(selisih))}</span>
                        </div>
                      );
                    })()}
                    {Number(req.jumlah_terbayar) > 0 && (
                      <div className="flex justify-between text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded mt-1">
                        <span>Telah Dibayar</span>
                        <span className="font-bold">- Rp {fmt(req.jumlah_terbayar)}</span>
                      </div>
                    )}
                  </div>

                  {/* Note */}
                  {req.catatan && (
                    <p className="text-[11px] text-slate-400 italic mb-4 line-clamp-1">
                      *Note: {req.catatan}
                    </p>
                  )}

                  {/* Bottom Cost & Detail Trigger */}
                  <div className="mt-auto border-t border-slate-100 pt-3.5 flex justify-between items-center">
                    <div>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                        {Number(req.jumlah_terbayar) > 0 ? 'Sisa Tagihan' : 'Jumlah Transfer'}
                      </p>
                      <p className="text-lg font-black text-slate-800">
                        Rp {fmt((req.custom_total_biaya !== undefined ? req.custom_total_biaya : (req.total_biaya || 0)) - (Number(req.jumlah_terbayar) || 0))}
                      </p>
                    </div>
                    {!isSelectionMode && (
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 text-rose-500 border-rose-200 hover:bg-rose-50 hover:text-rose-600 rounded-xl flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelection(req.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>

                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 text-[#8C6208] border-[#8C6208]/20 hover:bg-[#8C6208]/5 hover:text-[#8C6208] rounded-xl flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPO(req);
                          }}
                          title="Download / Print PO PDF"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>

                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 text-blue-500 border-blue-200 hover:bg-blue-50 hover:text-blue-600 rounded-xl flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(req);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>

                        {isFinance && req.status === 'Menunggu Pembayaran' ? (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setIsEditingTotalTransfer(false); setSelectedRequest(req); }}
                            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-xl h-auto transition-colors shadow-sm"
                          >
                            <CheckCircle2 className="w-4 h-4" /> Konfirmasi
                          </button>
                        ) : (
                          <Button 
                            onClick={(e) => { e.stopPropagation(); setIsEditingTotalTransfer(false); setSelectedRequest(req); }}
                            className="bg-slate-900 hover:bg-black text-white text-[11px] font-bold px-3 py-1.5 rounded-xl h-auto transition-colors"
                          >
                            Detail
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )})}
              </div>
            </div>
          ))}
          
          {/* ── Pagination Controls ── */}
          {groupedRequests.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(groupedRequests.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(groupedRequests.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(groupedRequests.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* Transfer Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-slate-900/60 -xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 relative border border-[#E6D5B8]/30">
            {/* Close Button */}
            <button 
              onClick={() => setSelectedRequest(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6 pt-8 text-center border-b border-[#E6D5B8]/20 bg-slate-50/40">
              <Badge className={`text-xs font-black uppercase border tracking-wider shadow-none mb-3 inline-flex items-center gap-1.5 ${
                selectedRequest.status === 'Menunggu Pembayaran' ? 'bg-amber-100/50 text-amber-700 border-amber-200' : 'bg-emerald-100/60 text-emerald-800 border-emerald-300'
              }`}>
                <span>{selectedRequest.status}</span>
                {selectedRequest.status === 'Sudah Dibayar' && getPaymentDateDisplay(selectedRequest) && (
                  <span className="font-extrabold text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-md tracking-normal">
                    Dibayar: {getPaymentDateDisplay(selectedRequest)}
                  </span>
                )}
              </Badge>
              <h2 className="text-lg font-black text-[#302621] mb-1">{selectedRequest.nama_item}</h2>
              <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400 font-black uppercase tracking-wider mb-4 flex-wrap">
                <span>PIC: {selectedRequest.pic}</span>
                {getRequestDateDisplay(selectedRequest) && (
                  <span>• Masuk Pengajuan: {getRequestDateDisplay(selectedRequest)} WIB</span>
                )}
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Item List (Batch or Single) */}
              {(() => {
                let items = [];
                 try { items = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : (selectedRequest.batch_items || []); } catch(e){}
                 if (items.length > 0) {
                   return (
                     <div className="bg-white border border-[#E6D5B8]/60 rounded-2xl p-4">
                       <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 border-b border-slate-100 pb-2">Daftar Item ({items.length})</p>
                       <div className="space-y-2 max-h-[150px] overflow-y-auto pr-2">
                          {items.map((it, idx) => {
                            const basePrice = (it.ppn_type === 'exclude' && it.harga_sebelum_ppn) 
                              ? parseFloat(it.harga_sebelum_ppn) 
                              : parseFloat(it.harga_rupiah || it.harga_beli || 0);

                            return (
                            <div key={idx} className="flex flex-col text-xs border-b border-slate-50 pb-2 mb-2 last:border-0 last:pb-0 last:mb-0">
                              <div className="flex justify-between items-start">
                                <div className="flex-1 truncate pr-2">
                                  <p className="font-bold text-slate-700">{it.name || it.nama || it.nama_barang}</p>
                                  <p className="text-[10px] text-slate-500">{it.qty} {it.satuan} × Rp {fmt(basePrice)}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <p className="font-bold text-slate-800 whitespace-nowrap">Rp {fmt((basePrice * (it.qty || 0)) - (it.diskon_nominal || 0))}</p>
                                  {editingItemIdx !== idx && (selectedRequest.status === 'Menunggu Pembayaran' || selectedRequest.status === 'Menunggu Konfirmasi Tempo' || selectedRequest.status === 'Barang Datang') && (
                                    <button 
                                      onClick={() => {
                                        setTempItemDiskon(it.diskon_nominal?.toString() || '');
                                        setTempItemDiskonPct('');
                                        setEditingItemIdx(idx);
                                      }}
                                      className="text-[9px] text-rose-400 hover:text-rose-600 font-semibold flex items-center gap-0.5 mt-0.5"
                                    >
                                      <Pencil className="w-2.5 h-2.5" /> {it.diskon_nominal > 0 ? 'Edit Diskon' : 'Diskon'}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {editingItemIdx === idx ? (
                                <div className="flex justify-between items-center text-[10px] text-rose-600 bg-rose-50/60 mt-1.5 px-2 py-1.5 rounded-md">
                                  <span className="font-semibold">Input Diskon:</span>
                                  <div className="flex items-center gap-1.5">
                                    <div className="relative flex items-center">
                                      <Input 
                                        type="number"
                                        value={tempItemDiskonPct}
                                        placeholder="0"
                                        className="h-6 w-14 text-[10px] font-bold text-right bg-white border-rose-200 focus-visible:ring-rose-400 p-1 pr-4"
                                        onChange={(e) => {
                                          setTempItemDiskonPct(e.target.value);
                                          const pct = parseFloat(e.target.value) || 0;
                                          const totalHarga = basePrice * (it.qty || 0);
                                          setTempItemDiskon(Math.round(totalHarga * (pct / 100)).toString());
                                        }}
                                      />
                                      <span className="absolute right-1 text-[9px]">%</span>
                                    </div>
                                    <span className="font-semibold">=</span>
                                    <Input 
                                      type="number"
                                      value={tempItemDiskon}
                                      onChange={(e) => setTempItemDiskon(e.target.value)}
                                      className="h-6 w-24 text-[10px] font-bold text-right bg-white border-rose-200 focus-visible:ring-rose-400 p-1"
                                      placeholder="Rp"
                                      autoFocus
                                    />
                                   <button
                                     onClick={() => {
                                       const parsedItems = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : (selectedRequest.batch_items || []);
                                       const newItems = [...parsedItems];
                                       newItems[idx].diskon_nominal = Number(tempItemDiskon) || 0;
                                       
                                       let newTotalBarang = 0;
                                       let ppnNominal = 0;
                                       for (const item of newItems) {
                                         const buyQty = item.qty || 0;
                                         const itemDiskon = item.diskon_nominal || 0;
                                         
                                         let baseTotal = 0;
                                         let ppnAmt = 0;
                                         if (item.ppn_type === 'exclude' && item.harga_sebelum_ppn) {
                                           const itemBasePrice = parseFloat(item.harga_sebelum_ppn);
                                           baseTotal = (buyQty * itemBasePrice) - itemDiskon;
                                           const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
                                           ppnAmt = baseTotal * (pct / 100);
                                         } else {
                                           const hargaUnit = parseFloat(item.harga_rupiah || item.harga_beli || 0);
                                           baseTotal = (buyQty * hargaUnit) - itemDiskon;
                                           if (item.ppn_type === 'exclude') {
                                             const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
                                             ppnAmt = baseTotal * (pct / 100);
                                           }
                                         }
                                         
                                         newTotalBarang += baseTotal;
                                         ppnNominal += ppnAmt;
                                       }
                                       const ongkirTransfer = (selectedRequest.ongkir > 0 && selectedRequest.ongkir_mode !== 'petty_cash') ? selectedRequest.ongkir : 0;
                                       const totalTf = newTotalBarang + ppnNominal + ongkirTransfer;
                                       
                                       setSelectedRequest(prev => ({
                                         ...prev,
                                         batch_items: JSON.stringify(newItems),
                                         total_barang: newTotalBarang,
                                         ppn_nominal: ppnNominal,
                                         total_biaya: totalTf,
                                         custom_total_biaya: totalTf
                                       }));
                                       setEditingItemIdx(null);
                                     }}
                                     className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-md flex items-center justify-center hover:bg-emerald-200"
                                   >
                                     <Check className="w-3 h-3" />
                                   </button>
                                   <button
                                     onClick={() => setEditingItemIdx(null)}
                                     className="w-6 h-6 bg-rose-100 text-rose-700 rounded-md flex items-center justify-center hover:bg-rose-200"
                                   >
                                     <X className="w-3 h-3" />
                                   </button>
                                 </div>
                               </div>
                             ) : (
                               it.diskon_nominal > 0 && (
                                 <div className="flex justify-between text-[10px] text-rose-600 bg-rose-50/60 px-2 py-1 rounded-md mt-1.5 w-full">
                                   <span className="font-semibold flex items-center gap-1">Diskon Item</span>
                                   <span className="font-bold">- Rp {fmt(it.diskon_nominal)}</span>
                                 </div>
                               )
                             )}
                            </div>
                            )})}
                        </div>
                     </div>
                   );
                 }
                 return null;
              })()}

              {/* Cost Breakdown */}
              {(() => {
                let totalDiskonItems = 0;
                let calcTotalBarang = selectedRequest.qty_beli * selectedRequest.harga_unit;
                let calcPpnNominal = 0;
                let isBatch = false;
                let items = [];

                try {
                  items = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : (selectedRequest.batch_items || []);
                  if (items.length > 0) {
                    isBatch = true;
                    calcTotalBarang = 0;
                    totalDiskonItems = items.reduce((sum, item) => sum + (item.diskon_nominal || 0), 0);
                    
                    for (const item of items) {
                      const buyQty = item.qty || 0;
                      const itemDiskon = item.diskon_nominal || 0;
                      let baseTotal = 0;
                      let ppnAmt = 0;
                      if (item.ppn_type === 'exclude' && item.harga_sebelum_ppn) {
                        const basePrice = parseFloat(item.harga_sebelum_ppn);
                        baseTotal = (buyQty * basePrice) - itemDiskon;
                        const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
                        ppnAmt = baseTotal * (pct / 100);
                      } else {
                        const hargaUnit = parseFloat(item.harga_rupiah || item.harga_beli || 0);
                        baseTotal = (buyQty * hargaUnit) - itemDiskon;
                        if (item.ppn_type === 'exclude') {
                          const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
                          ppnAmt = baseTotal * (pct / 100);
                        }
                      }
                      calcTotalBarang += baseTotal;
                      calcPpnNominal += ppnAmt;
                    }
                  }
                } catch(e){}

                // Gunakan total_barang dan persentase PPN yang presisi dari subtotal agar tidak ada selisih pembulatan unit
                const subtotalBarang = selectedRequest.total_barang || calcTotalBarang;
                const ppnPct = selectedRequest.ppn_percentage || (selectedRequest.ppn_nominal > 0 ? 11 : 0);
                const hasPpn = selectedRequest.ppn_nominal > 0 || selectedRequest.ppn_status === 'exclude' || ppnPct > 0;
                const ppnNominal = hasPpn ? Math.round(subtotalBarang * (ppnPct / 100)) : 0;
                const subtotalSetelahPpn = subtotalBarang + ppnNominal;
                const ongkirTransfer = (selectedRequest.ongkir > 0 && selectedRequest.ongkir_mode !== 'petty_cash') ? selectedRequest.ongkir : 0;
                const totalTf = subtotalSetelahPpn + ongkirTransfer;

                return (
                  <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 rounded-2xl p-4 space-y-2.5">
                    {/* 1. Harga Barang */}
                    {(!selectedRequest.nama_item?.includes('Batch Order') && (!items || items.length <= 1)) && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Qty × Harga Satuan</span>
                        <span className="font-bold text-slate-700">{selectedRequest.qty_beli} × Rp {fmt(selectedRequest.harga_unit)}</span>
                      </div>
                    )}
                    {totalDiskonItems > 0 && (
                      <div className="flex justify-between text-xs text-rose-600 bg-rose-50/60 -mx-1 px-1 py-1 rounded-lg">
                        <span className="font-semibold flex items-center gap-1">Total Diskon Item</span>
                        <span className="font-bold">- Rp {fmt(totalDiskonItems)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Harga Barang (Setelah Diskon)</span>
                      <span className="font-bold text-slate-700">Rp {fmt(subtotalBarang)}</span>
                    </div>

                    {/* 2. PPN */}
                    {hasPpn && (
                      <div className="flex justify-between text-xs text-amber-700 bg-amber-50/60 -mx-1 px-1 py-1 rounded-lg">
                        <span className="font-semibold flex items-center gap-1">
                          PPN {ppnPct}%
                        </span>
                        <span className="font-bold">+ Rp {fmt(ppnNominal)}</span>
                      </div>
                    )}

                    {/* 3. Subtotal setelah PPN */}
                    {hasPpn && (
                      <div className="flex justify-between text-xs border-t border-dashed border-[#E6D5B8]/60 pt-2">
                        <span className="text-slate-500 font-semibold">Subtotal (Barang + PPN)</span>
                        <span className="font-bold text-slate-800">Rp {fmt(subtotalSetelahPpn)}</span>
                      </div>
                    )}

                    {/* 4. Ongkir */}
                    {selectedRequest.ongkir > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500 flex items-center gap-1">
                          Ongkir
                          <Badge variant="outline" className="text-[8px] py-0 px-1.5">
                            {selectedRequest.ongkir_mode === 'petty_cash' ? 'Petty Cash' : 'Transfer'}
                          </Badge>
                        </span>
                        <span className={`font-bold ${selectedRequest.ongkir_mode === 'petty_cash' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                          + Rp {fmt(selectedRequest.ongkir)}
                        </span>
                      </div>
                    )}

                    {/* Penyesuaian Harga (Biaya Tambahan atau Diskon Tambahan) */}
                    {(() => {
                      const finalDisplayedTransfer = selectedRequest.custom_total_biaya !== undefined && selectedRequest.custom_total_biaya !== null ? selectedRequest.custom_total_biaya : selectedRequest.total_biaya;
                      const selisih = totalTf - finalDisplayedTransfer;
                      const isDiskon = selisih > 0;
                      const absSelisih = Math.abs(selisih);
                      
                      if (absSelisih > 0 && !isEditingDiskonNominalDetail) {
                        return (
                        <div className={`flex justify-between text-xs -mx-1 px-1 py-1 rounded-lg ${isDiskon ? 'text-rose-600 bg-rose-50/60' : 'text-amber-600 bg-amber-50/60'}`}>
                          <span className="font-semibold flex items-center gap-1">
                            {isEditingDiskonNamaDetail ? (
                              <Input
                                value={tempDiskonNamaDetail}
                                onChange={(e) => setTempDiskonNamaDetail(e.target.value)}
                                onBlur={() => {
                                  setSelectedRequest(prev => ({ ...prev, diskon_nama_temp: tempDiskonNamaDetail }));
                                  setIsEditingDiskonNamaDetail(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    setSelectedRequest(prev => ({ ...prev, diskon_nama_temp: tempDiskonNamaDetail }));
                                    setIsEditingDiskonNamaDetail(false);
                                  }
                                }}
                                autoFocus
                                className={`h-6 w-32 text-xs bg-white p-1 ${isDiskon ? 'border-rose-200 focus-visible:ring-rose-400' : 'border-amber-200 focus-visible:ring-amber-400'}`}
                              />
                            ) : (
                              <div className="flex items-center gap-1 cursor-pointer hover:underline" onClick={() => { setIsEditingDiskonNamaDetail(true); setTempDiskonNamaDetail(selectedRequest.diskon_nama_temp || (isDiskon ? 'Diskon Tambahan' : 'Biaya Tambahan')); }}>
                                {selectedRequest.diskon_nama_temp || (isDiskon ? 'Diskon Tambahan' : 'Biaya Tambahan')}
                                <Pencil className={`w-3 h-3 ${isDiskon ? 'text-rose-400' : 'text-amber-400'}`} />
                              </div>
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{isDiskon ? '-' : '+'} Rp {fmt(absSelisih)}</span>
                            <button
                              onClick={() => {
                                setTempDiskonNominalDetail(selisih.toString());
                                setTempDiskonNominalDetailPct('');
                                setIsEditingDiskonNominalDetail(true);
                              }}
                              className={`p-1 transition-colors ${isDiskon ? 'text-rose-400 hover:text-rose-600' : 'text-amber-400 hover:text-amber-600'}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                      }
                      return null;
                    })()}
                    
                    {/* Add/Edit Diskon Input Form */}
                    {isEditingDiskonNominalDetail && (
                      <div className="flex justify-between items-center text-xs text-slate-600 bg-slate-50/60 -mx-1 px-1 py-1 rounded-lg">
                        <span className="font-semibold">Input Penyesuaian:</span>
                        <div className="flex items-center gap-2">
                          <div className="relative flex items-center">
                            <Input 
                              type="number"
                              value={tempDiskonNominalDetailPct}
                              placeholder="0"
                              className="h-7 w-16 text-xs font-bold text-right pr-4"
                              onChange={(e) => {
                                setTempDiskonNominalDetailPct(e.target.value);
                                const pct = parseFloat(e.target.value) || 0;
                                const total = selectedRequest.total_biaya || 0;
                                setTempDiskonNominalDetail(Math.round(total * (pct / 100)).toString());
                              }}
                            />
                            <span className="absolute right-1.5 text-[10px] font-bold">%</span>
                          </div>
                          <span className="font-semibold">=</span>
                          <Input 
                            type="number"
                            value={tempDiskonNominalDetail}
                            onChange={(e) => setTempDiskonNominalDetail(e.target.value)}
                            className="h-7 w-24 text-xs font-bold text-right"
                            autoFocus
                            placeholder="Rp"
                          />
                          <button
                            onClick={() => {
                              const newDiskon = Number(tempDiskonNominalDetail) || 0;
                              const newCustomTotal = totalTf - newDiskon;
                              setSelectedRequest(prev => ({ 
                                ...prev, 
                                custom_total_biaya: newCustomTotal,
                                diskon_nama_temp: prev.diskon_nama_temp || 'Diskon Pembelian'
                              }));
                              setIsEditingDiskonNominalDetail(false);
                            }}
                            className="w-7 h-7 bg-emerald-100 text-emerald-700 rounded-md flex items-center justify-center hover:bg-emerald-200"
                          >
                            <Check className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => setIsEditingDiskonNominalDetail(false)}
                            className="w-7 h-7 bg-rose-100 text-rose-700 rounded-md flex items-center justify-center hover:bg-rose-200"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Diskon Add Button (if no discount yet) */}
                    {(() => {
                      const finalDisplayedTransfer = selectedRequest.custom_total_biaya !== undefined && selectedRequest.custom_total_biaya !== null ? selectedRequest.custom_total_biaya : selectedRequest.total_biaya;
                      const selisih = totalTf - finalDisplayedTransfer;
                      if (selisih === 0 && !isEditingDiskonNominalDetail) {
                        return (
                          <div className="flex justify-end pt-1">
                            <button
                          onClick={() => {
                            setTempDiskonNominalDetail('');
                            setTempDiskonNominalDetailPct('');
                            setIsEditingDiskonNominalDetail(true);
                          }}
                          className="text-[10px] text-rose-500 font-bold hover:underline flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Tambah Diskon Pembelian
                        </button>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* 5. Total Transfer */}
                    <div className="border-t border-[#E6D5B8]/60 pt-2 flex justify-between items-center">
                      <span className="text-xs font-black text-slate-800 uppercase">
                        {Number(selectedRequest.jumlah_terbayar) > 0 ? 'Sisa Tagihan' : 'Jumlah Transfer'}
                      </span>
                      
                      {isEditingTotalTransfer ? (
                        <div className="flex items-center gap-2">
                          <Input 
                            type="number"
                            value={tempTotalTransfer}
                            onChange={(e) => setTempTotalTransfer(e.target.value)}
                            className="h-8 w-32 font-bold text-right"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              setSelectedRequest(prev => ({ ...prev, custom_total_biaya: Number(tempTotalTransfer) }));
                              setIsEditingTotalTransfer(false);
                            }}
                            className="w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center hover:bg-emerald-200 transition-colors"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black text-[#8C6208]">
                            Rp {fmt((selectedRequest.custom_total_biaya !== undefined && selectedRequest.custom_total_biaya !== null ? selectedRequest.custom_total_biaya : selectedRequest.total_biaya) - (Number(selectedRequest.jumlah_terbayar) || 0))}
                          </span>
                          {(selectedRequest.status === 'Menunggu Pembayaran' || selectedRequest.status === 'Menunggu Konfirmasi Tempo') && (
                            <button
                              onClick={() => {
                                setTempTotalTransfer((selectedRequest.custom_total_biaya !== undefined ? selectedRequest.custom_total_biaya : selectedRequest.total_biaya).toString());
                                setIsEditingTotalTransfer(true);
                              }}
                              className="p-1.5 text-slate-400 hover:text-[#8C6208] transition-colors bg-white border border-slate-200 rounded-lg shadow-sm"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Inner Transfer Card */}
              <div className="bg-white border border-[#E6D5B8]/80 rounded-2xl p-5 space-y-4 shadow-xs relative">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-black text-[#302621]">
                    <Landmark className="w-5 h-5 text-[#8C6208]" />
                    <span>Transfer Bank {selectedRequest.bank_tujuan}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Rekening Tujuan</span>
                </div>

                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Nomor Rekening</p>
                  <div className="flex justify-between items-center bg-slate-50/80 px-3.5 py-2.5 rounded-xl border border-slate-200/50">
                    <span className="font-mono font-black text-slate-800 text-lg tracking-wide">{selectedRequest.nomor_rekening}</span>
                    <button 
                      onClick={() => handleCopyText(selectedRequest.nomor_rekening)}
                      className="text-xs font-bold text-[#8C6208] hover:underline uppercase tracking-wider"
                    >
                      Salin
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Atas Nama</p>
                  <p className="font-black text-slate-800 text-sm">{selectedRequest.atas_nama}</p>
                </div>

                {/* Sumber Rekening Pembayar (Bank Kita) */}
                {(selectedRequest.status === 'Sudah Dibayar' || selectedRequest.status === 'Dicicil') && (
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500 font-bold flex items-center gap-1.5">
                      <CreditCard className="w-4 h-4 text-emerald-600" />
                      Dibayar Menggunakan:
                    </span>
                    <span className="font-black text-emerald-800 bg-emerald-100/90 border border-emerald-200 px-2.5 py-1 rounded-lg text-xs">
                      {getPayingBank(selectedRequest)}
                    </span>
                  </div>
                )}
              </div>

              {/* Message from ASA (Blue alert box) */}
              <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-4 flex gap-3 text-left">
                <div className="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 text-blue-600">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-blue-900 mb-0.5">Pesan dari ASA:</p>
                  <p className="text-xs text-blue-800 leading-normal">
                    Jangan lupa bukti pembayaran di-screenshot ya kaakkk
                  </p>
                </div>
              </div>

              {/* Notice note */}
              <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 rounded-2xl p-4 flex gap-3 text-left">
                <div className="w-5 h-5 rounded-full bg-[#8C6208]/10 text-[#8C6208] flex items-center justify-center shrink-0">
                  <Info className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-[#7A695B] leading-normal font-medium">
                  {selectedRequest.status === 'Sudah Dibayar' ? (
                    <>Pembayaran telah dikonfirmasi dan lunas. Transaksi sudah otomatis tercatat di <strong>Biaya Produksi</strong> serta <strong>Mutasi Rekening</strong>.</>
                  ) : selectedRequest.status === 'Dicicil' ? (
                    <>Pembayaran sebagian telah dikonfirmasi. Sisa tagihan tercatat di <strong>Hutang</strong>. Transaksi sudah otomatis tercatat di <strong>Biaya Produksi</strong> serta <strong>Mutasi Rekening</strong>.</>
                  ) : selectedRequest.status === 'Menunggu Konfirmasi Tempo' || selectedRequest.metode_pembayaran === 'Tempo' ? (
                    <>Tagihan tempo akan dicatat otomatis di <strong>Hutang</strong> saat disetujui. Setelah lunas, transaksi akan tercatat di <strong>Mutasi Rekening</strong>.</>
                  ) : (
                    <>Setelah konfirmasi, status akan menjadi <strong>Sudah Dibayar</strong> (Atau <strong>Dicicil</strong> jika bayar sebagian). Transaksi otomatis tercatat di <strong>Biaya Produksi</strong> serta <strong>Mutasi Rekening</strong>.</>
                  )}
                </p>
              </div>

              {/* Download PO & Pecah Invoice Buttons */}
              {selectedRequest.batch_items && (() => {
                const bItems = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : (selectedRequest.batch_items || []);
                return (
                  <div className="flex gap-2 w-full">
                    <Button 
                      onClick={() => handleDownloadPO(selectedRequest)}
                      variant="outline"
                      className="flex-1 font-bold h-11 rounded-xl text-sm border-[#8C6208]/30 text-[#8C6208] hover:bg-[#8C6208]/5 flex items-center justify-center gap-2"
                    >
                      <Printer className="w-4 h-4" />
                      PO (PDF)
                    </Button>
                    {bItems.length > 1 && (
                      <Button 
                        onClick={() => {
                          setSplitSelectedItems(new Set());
                          setIsSplitModalOpen(true);
                        }}
                        variant="outline"
                        className="flex-1 font-bold h-11 rounded-xl text-sm border-blue-200 text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-2"
                      >
                        Pecah Invoice
                      </Button>
                    )}
                  </div>
                );
              })()}

              {/* Action Confirm Button */}
              <div className="flex flex-col gap-2">
                {selectedRequest.status === 'Menunggu Konfirmasi Tempo' ? (
                  <div className="flex gap-2 w-full">
                    <Button 
                      onClick={handleRejectTempo}
                      disabled={updateRequestMut.isPending || updateRequestMut.isLoading}
                      variant="outline"
                      className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-bold h-12 rounded-xl text-sm"
                    >
                      <X className="w-4 h-4 mr-2" /> Tolak
                    </Button>
                    <Button 
                      onClick={handleOpenTempoConfirm}
                      disabled={updateRequestMut.isPending || updateRequestMut.isLoading}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold h-12 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-md"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Setujui Tempo
                    </Button>
                  </div>
                ) : selectedRequest.status !== 'Sudah Dibayar' ? (
                  <div className="flex gap-2 w-full">
                    <Button 
                      onClick={async () => {
                        if (editingItemIdx !== null || isEditingDiskonNominalDetail || isEditingTotalTransfer) {
                          toast.error('Harap klik tanda centang (✔) terlebih dahulu pada diskon/nilai yang sedang Anda edit sebelum menyimpan.');
                          return;
                        }
                        try {
                          await updateRequestMut.mutateAsync({ 
                            id: selectedRequest.id, 
                            data: { 
                              batch_items: selectedRequest.batch_items,
                              total_barang: selectedRequest.total_barang,
                              ppn_nominal: selectedRequest.ppn_nominal,
                              total_biaya: selectedRequest.total_biaya,
                              custom_total_biaya: selectedRequest.custom_total_biaya,
                              diskon_nama_temp: selectedRequest.diskon_nama_temp
                            } 
                          });
                          toast.success('Perubahan berhasil disimpan');
                        } catch (err) {
                          toast.error('Gagal menyimpan perubahan');
                        }
                      }}
                      disabled={updateRequestMut.isPending || updateRequestMut.isLoading}
                      variant="outline"
                      className="flex-1 border-[#302621] text-[#302621] hover:bg-slate-50 font-bold h-12 rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Simpan Perubahan
                    </Button>
                    <Button 
                      onClick={handleOpenMutasiConfirm}
                      disabled={updateRequestMut.isPending || updateRequestMut.isLoading}
                      className="flex-[1.5] bg-[#302621] hover:bg-black text-white font-bold h-12 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-md"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Konfirmasi Bayar
                    </Button>
                  </div>
                ) : (
                  <Button 
                    disabled
                    className="w-full bg-emerald-600 text-white font-bold h-12 rounded-xl text-sm opacity-100 flex items-center justify-center gap-2 cursor-default"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Pembayaran Sudah Dikonfirmasi
                  </Button>
                )}

                {/* Delivery Button (Purchasing confirms arrival) */}
                {selectedRequest.status === 'Dalam Pengiriman' ? (
                  <Button 
                    onClick={() => setArrivalConfirmData(selectedRequest)}
                    disabled={updateRequestMut.isPending || updateRequestMut.isLoading || isProcessingArrivalModal}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-md"
                  >
                    ✓ Konfirmasi Barang Datang
                  </Button>
                ) : selectedRequest.status === 'Barang Datang' && selectedRequest.status !== 'Sudah Dibayar' ? (
                  <Button 
                    disabled
                    className="w-full bg-blue-600/50 text-white font-bold h-10 rounded-xl text-xs opacity-100 flex items-center justify-center gap-2 cursor-default"
                  >
                    ✓ Barang Sudah Datang
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mutasi Confirmation Modal */}
      {showMutasiConfirm && selectedRequest && (
        <div className="fixed inset-0 bg-slate-900/60 -xs z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center relative">
              <button 
                onClick={() => setShowMutasiConfirm(false)}
                className="absolute top-4 right-4 p-1.5 text-white/70 hover:text-white rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden border border-white/30">
                <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
              </div>
              <h2 className="text-xl font-black mb-1">Pesan dari ASA</h2>
              <p className="text-sm text-blue-100 font-medium">Pembayaran akan otomatis dicatat ke Mutasi Rekening dan Biaya Produksi.</p>
            </div>

            <div className="p-6 space-y-5 bg-slate-50/50">
              <div className="space-y-4 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Debit dari Rekening</label>
                  <Select value={mutasiAccount} onValueChange={setMutasiAccount}>
                    <SelectTrigger className="h-10 border-slate-200 font-bold text-slate-700 bg-slate-50">
                      <SelectValue placeholder="Pilih Rekening" />
                    </SelectTrigger>
                    <SelectContent className="z-[200]">
                      <SelectItem value="bca">{accounts?.bca?.name || 'BCA'}</SelectItem>
                      <SelectItem value="mandiri">{accounts?.mandiri?.name || 'Mandiri'}</SelectItem>
                      <SelectItem value="bni">{accounts?.bni?.name || 'BNI'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Tanggal Pembayaran</label>
                  <CustomDatePicker
                    value={mutasiDate}
                    onChange={setMutasiDate}
                    className="h-10 border-slate-200"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Total Mutasi Keluar (Rp)</label>
                  {isEditingMutasi ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        type="number" 
                        value={tempMutasiAmount}
                        onChange={(e) => setTempMutasiAmount(e.target.value)}
                        className="h-10 font-bold"
                        autoFocus
                      />
                      <button 
                        onClick={() => {
                          setMutasiAmount(Number(tempMutasiAmount));
                          setIsEditingMutasi(false);
                        }}
                        className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center hover:bg-emerald-200 transition-colors"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                      <span className="font-black text-lg text-[#8C6208]">Rp {fmt(mutasiAmount)}</span>
                      <button 
                        onClick={() => {
                          setTempMutasiAmount(mutasiAmount.toString());
                          setIsEditingMutasi(true);
                        }}
                        className="p-1.5 text-slate-400 hover:text-[#8C6208] transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    *Default otomatis diisi dari total biaya pengajuan (termasuk ongkir).
                  </p>
                </div>
                
                {selectedRequest && mutasiAmount < (selectedRequest.total_biaya || 0) && (
                  <div className="pt-2 border-t border-slate-100 animate-in fade-in zoom-in slide-in-from-top-2 duration-300">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2.5">
                      <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] font-bold text-amber-800">Pembayaran Sebagian (Dicicil)</p>
                        <p className="text-[10px] text-amber-700/90 mt-0.5 leading-relaxed">
                          Kamu memasukkan nominal lebih kecil. Sisa tagihan sebesar <strong className="font-bold">Rp {fmt((selectedRequest.total_biaya || 0) - mutasiAmount)}</strong> akan dicatat sebagai Sisa Hutang dan status menjadi "Dicicil".
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowMutasiConfirm(false)}
                  variant="outline" 
                  className="flex-1 h-12 rounded-xl font-bold border-slate-200"
                >
                  Batal
                </Button>
                <Button 
                  onClick={handleFinalConfirm}
                  disabled={updateRequestMut.isPending || updateRequestMut.isLoading}
                  className="flex-1 h-12 rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> Konfirmasi & Catat Mutasi
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tempo Confirmation Modal */}
      {showTempoConfirm && selectedRequest && (
        <div className="fixed inset-0 bg-slate-900/60 -xs z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white text-center relative">
              <button 
                onClick={() => setShowTempoConfirm(false)}
                className="absolute top-4 right-4 p-1.5 text-white/70 hover:text-white rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden border border-white/30">
                <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
              </div>
              <h2 className="text-xl font-black mb-1">Pesan dari ASA</h2>
              <p className="text-sm text-amber-100 font-medium">Tagihan ini akan otomatis dicatat sebagai Hutang. Pilih kategori COA untuk hutang ini.</p>
            </div>

            <div className="p-6 space-y-5 bg-slate-50/50">
              <div className="space-y-4 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Kategori Akun (COA)</label>
                  <Select value={tempoAccount} onValueChange={setTempoAccount}>
                    <SelectTrigger className="h-10 border-slate-200 font-bold text-slate-700 bg-slate-50">
                      <SelectValue placeholder="Pilih Kategori Hutang/Biaya" />
                    </SelectTrigger>
                    <SelectContent className="z-[200]">
                      <SelectItem value="Biaya Produksi - Bahan Baku">Biaya Produksi - Bahan Baku</SelectItem>
                      <SelectItem value="Biaya Produksi - Kemasan">Biaya Produksi - Kemasan</SelectItem>
                      <SelectItem value="Hutang Usaha">Hutang Usaha</SelectItem>
                      <SelectItem value="Hutang Operasional">Hutang Operasional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Nominal Tagihan (Rp)</label>
                  <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                    <span className="font-black text-lg text-slate-700">Rp {fmt(selectedRequest.custom_total_biaya !== undefined ? selectedRequest.custom_total_biaya : (selectedRequest.total_biaya || 0))}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowTempoConfirm(false)}
                  variant="outline" 
                  className="flex-1 h-12 rounded-xl font-bold border-slate-200"
                >
                  Batal
                </Button>
                <Button 
                  onClick={handleFinalConfirmTempo}
                  disabled={updateRequestMut.isPending || updateRequestMut.isLoading}
                  className="flex-1 h-12 rounded-xl font-bold bg-amber-500 hover:bg-amber-600 text-white gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> Simpan & Setujui Tempo
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Split Invoice Modal */}
      {isSplitModalOpen && selectedRequest && (() => {
        const items = typeof selectedRequest.batch_items === 'string' ? JSON.parse(selectedRequest.batch_items) : (selectedRequest.batch_items || []);
        return (
          <div className="fixed inset-0 bg-slate-900/60 -xs z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
              <div className="bg-blue-600 p-5 text-white flex justify-between items-center relative">
                <h2 className="text-lg font-black">Pecah Invoice (Split Batch)</h2>
                <button 
                  onClick={() => setIsSplitModalOpen(false)}
                  className="p-1.5 text-white/70 hover:text-white rounded-full transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <p className="text-xs text-slate-500 mb-4">
                  Pilih item yang ingin dipindahkan ke <strong>Invoice Baru</strong>. Item yang tidak diceklis akan tetap berada di invoice saat ini.
                </p>
                <div className="space-y-3">
                  {items.map((it, idx) => {
                    const id = it.inventory_item_id || it.id || it.name || it.nama || it.nama_barang;
                    const isSelected = splitSelectedItems.has(id);
                    return (
                      <div key={idx} className={`p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${isSelected ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`} onClick={() => {
                        const next = new Set(splitSelectedItems);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        setSplitSelectedItems(next);
                      }}>
                        <Checkbox checked={isSelected} />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-slate-800">{it.name || it.nama || it.nama_barang}</p>
                          <p className="text-xs text-slate-500">{it.qty || it.buyQty || it.qty_beli} {it.satuan || ''} x Rp {fmt(it.harga_rupiah || it.harga_beli || 0)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="p-5 border-t border-slate-100 flex gap-3 bg-slate-50">
                <Button 
                  onClick={() => setIsSplitModalOpen(false)}
                  variant="outline" 
                  className="flex-1 h-11 rounded-xl font-bold"
                >
                  Batal
                </Button>
                <Button 
                  onClick={handleConfirmSplit}
                  disabled={splitSelectedItems.size === 0 || splitSelectedItems.size === items.length || updateRequestMut.isPending || createRequestMut.isPending}
                  className="flex-1 h-11 rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Pecah & Simpan
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ASA Arrival Confirmation Pop-up Modal */}
      <AsaArrivalConfirmModal
        isOpen={!!arrivalConfirmData}
        onClose={() => setArrivalConfirmData(null)}
        requestData={arrivalConfirmData}
        inventoryMaster={{
          bahanCairList,
          botolList,
          tutupList,
          sprayList,
          fullsetList,
          itemTambahanList
        }}
        isProcessing={isProcessingArrivalModal}
        onConfirm={async ({ ekspedisi, catatan, items }) => {
          if (!arrivalConfirmData) return;
          setIsProcessingArrivalModal(true);
          try {
            // 1. Update status
            await updateRequestMut.mutateAsync({ id: arrivalConfirmData.id, data: { status: 'Barang Datang' } });

            // 2. Tambah stok fisik di master inventori
            for (const it of items) {
              const qtyToAdd = it.incomingQty || 0;
              const itemName = (it.nama || '').trim().toLowerCase();
              const itemType = (it.type || it.kategori || '').toLowerCase();
              const itemId = it.id;

              if (itemType.includes('cair') || itemType.includes('bibit') || itemType.includes('campuran') || itemType === 'bahan-cair') {
                const match = bahanCairList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
                if (match) {
                  const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
                  const newStok = Number((curStok + qtyToAdd).toFixed(2));
                  await base44.entities.BahanCair.update(match.id, { stok: newStok });
                }
              } else if (itemType.includes('botol')) {
                const match = botolList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
                if (match) {
                  const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
                  await base44.entities.Botol.update(match.id, { stok: curStok + qtyToAdd });
                }
              } else if (itemType.includes('tutup')) {
                const match = tutupList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
                if (match) {
                  const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
                  await base44.entities.Tutup.update(match.id, { stok: curStok + qtyToAdd });
                }
              } else if (itemType.includes('spray')) {
                const match = sprayList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
                if (match) {
                  const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
                  await base44.entities.Spray.update(match.id, { stok: curStok + qtyToAdd });
                }
              } else if (itemType.includes('fullset')) {
                const match = fullsetList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
                if (match) {
                  const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
                  await base44.entities.Fullset.update(match.id, { stok: curStok + qtyToAdd });
                }
              } else {
                const match = itemTambahanList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
                if (match) {
                  const curStok = parseFloat(String(match.stok || 0).replace(',', '.')) || 0;
                  await base44.entities.ItemTambahan.update(match.id, { stok: curStok + qtyToAdd });
                }
              }
            }

            // 3. Buat transaksi BarangMasuk resmi
            const refNumber = (arrivalConfirmData.po_number || arrivalConfirmData.id || '').trim();
            const bmItems = items.map(it => ({
              nama_barang: it.nama,
              jumlah: it.incomingQty,
              satuan: it.satuan,
              kategori: it.kategori || it.masterCategory || 'Bahan',
              harga_satuan: it.harga || 0
            }));

            await base44.entities.BarangMasuk.create({
              tanggal: new Date().toISOString().split('T')[0],
              nomor_referensi: refNumber,
              supplier: arrivalConfirmData.nama_perusahaan || arrivalConfirmData.vendor || arrivalConfirmData.supplier || 'Supplier',
              ekspedisi: ekspedisi || 'Ekspedisi Vendor / Langsung',
              ongkir: arrivalConfirmData.ongkir || 0,
              ongkir_dibayar_oleh: 'Perusahaan',
              catatan: catatan || arrivalConfirmData.catatan || `Barang Masuk via PO ${refNumber}`,
              pic: arrivalConfirmData.pic || member?.full_name || 'Purchasing Staff',
              input_by: member?.email || 'System',
              items: bmItems
            });

            // 4. Invalidate queries
            queryClient.invalidateQueries({ queryKey: ['barang-masuk'] });
            queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
            queryClient.invalidateQueries({ queryKey: ['bahan-cair'] });
            queryClient.invalidateQueries({ queryKey: ['botol'] });
            queryClient.invalidateQueries({ queryKey: ['tutup'] });
            queryClient.invalidateQueries({ queryKey: ['spray'] });
            queryClient.invalidateQueries({ queryKey: ['fullset'] });
            queryClient.invalidateQueries({ queryKey: ['item-tambahan'] });

            if (selectedRequest?.id === arrivalConfirmData.id) {
              setSelectedRequest(prev => ({ ...prev, status: 'Barang Datang' }));
            }
            setArrivalConfirmData(null);
            toast.success('✅ Kedatangan barang berhasil dikonfirmasi! Stok gudang telah bertambah & dicatat di Delivery.');
          } catch (err) {
            toast.error('Gagal memproses kedatangan: ' + err.message);
          } finally {
            setIsProcessingArrivalModal(false);
          }
        }}
      />

      {/* HIDDEN PRINT COMPONENT */}
    </div>
    <POPrint data={printData} />
    </>
  );
}
